import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Handle,
    Position,
    MarkerType,
    MiniMap,
    useReactFlow,
} from 'reactflow';

// Import React Flow CSS
import 'reactflow/dist/style.css';

// Import Lucide Icons - Consolidated List
import {
    LayoutDashboard, Mail, Search, Bell, ChevronDown, Menu, Globe, LifeBuoy,
    Star, Target, Briefcase, Brain, Settings as SettingsIcon, Loader2, Info, X, Database, Upload, Send, Copy, Check,
    MessageSquare,   // Icon for Communication Style
    ShieldCheck,     // Icon for Constraints & Ethics / Firewall / Clone confirmed
    ShieldAlert,     // Icon for Errors / Default Data Warning
    ImageIcon, // Icon for Logo URL
    MousePointerClick, // Icon for selection instruction
    Link as LinkIcon,  // Icon for connection instruction (alternative)
    Share2,          // Icon for Connect/Hierarchy & Graph section title / Clone Option
    UserCheck,       // Icon for Parent selection
    ChevronRight,    // Used in Radio buttons
    CircleDot,       // Radio checked
    Circle,          // Radio unchecked
    Network,         // Icon for Agent Swarm
    Box,             // Icon for Single Item / Single Dataset
    Eye,             // Icon for Show Secret / View Content
    EyeOff,          // Icon for Hide Secret
    DollarSign,      // Icon for Pricing
    Cpu,             // Icon for AI Model
    ExternalLink,    // Icon for external link
    FileText,        // Icon for Sections / Start from Scratch
    Puzzle,          // Icon for Chunks
    Binary,          // Icon for Tokens
    Gauge,           // Icon for Average Length
    BookOpen,        // Icon for View Content
    MinusSquare,     // Icon for Collapse
    PlusSquare,      // Icon for Expand / Create Another Agent
    Users,           // Icon for multiple datasets in hierarchy
    ListChecks,      // Icon for Firewall features
    AlertTriangle,   // Icon for Default Agent Warning / General Warning
    GitBranch,       // Icon for Clone Agent Option
    ArrowLeft,       // Icon for Cancel/Back
} from 'lucide-react'; // Added AlertTriangle, GitBranch, ArrowLeft


// --- Reusable Clickable Components (Enhanced Styles) ---
const ClickableLink = React.forwardRef(({ children, className = '', href = '#', target, rel, onClick, disabled, title, variant = 'default', size = 'md', icon: Icon, iconPosition = 'left', ...props }, ref) => {
    const baseStyle = 'inline-flex items-center justify-center gap-2 rounded-md border font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-all duration-150 ease-in-out';
    const sizeStyles = {
        sm: 'px-2.5 py-1 text-xs',
        md: 'px-4 py-1.5 text-sm',
        lg: 'px-5 py-2 text-base',
    };
    const variantStyles = {
        default: `border-transparent text-purple-400 hover:text-purple-300 hover:underline focus-visible:underline ${disabled ? 'text-gray-600 hover:text-gray-600' : ''}`,
        primary: `border-transparent bg-purple-600 text-white hover:bg-purple-500 focus-visible:bg-purple-700 ${disabled ? 'bg-gray-700 text-gray-400 hover:bg-gray-700' : ''}`,
        secondary: `border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 focus-visible:border-gray-500 ${disabled ? 'border-gray-700 bg-gray-800 text-gray-500 hover:bg-gray-800' : ''}`,
        danger: `border-transparent bg-red-600 text-white hover:bg-red-500 focus-visible:bg-red-700 ${disabled ? 'bg-red-800 text-red-300 hover:bg-red-800' : ''}`,
        warning: `border-transparent bg-yellow-500 text-black hover:bg-yellow-400 focus-visible:bg-yellow-600 ${disabled ? 'bg-yellow-700 text-yellow-200 hover:bg-yellow-700' : ''}`,
        ghost: `border-transparent bg-transparent text-gray-400 hover:bg-gray-800/50 hover:text-gray-200 focus-visible:bg-gray-700/50 ${disabled ? 'text-gray-600 hover:bg-transparent hover:text-gray-600' : ''}`,
    };

    const iconSize = size === 'sm' ? 14 : size === 'lg' ? 18 : 16;

    return (
        <a
            ref={ref}
            href={!disabled ? href : undefined}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : rel}
            onClick={!disabled ? onClick : (e) => e.preventDefault()}
            role="link"
            tabIndex={disabled ? -1 : 0}
            className={`${baseStyle} ${sizeStyles[size]} ${variantStyles[variant]} ${disabled ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer'} ${className}`}
            aria-disabled={disabled}
            title={title || (typeof children === 'string' ? children : '')}
            {...props}
        >
             {Icon && iconPosition === 'left' && <Icon size={iconSize} aria-hidden="true" />}
            {children}
             {Icon && iconPosition === 'right' && <Icon size={iconSize} aria-hidden="true" />}
        </a>
    );
});
ClickableLink.displayName = 'ClickableLink';


const ClickableDiv = React.forwardRef(({ children, className = '', onClick, disabled, title, variant = 'primary', size = 'md', icon: Icon, iconPosition = 'left', loading, ...props }, ref) => {
     const baseStyle = 'inline-flex items-center justify-center gap-2 rounded-md border font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-all duration-150 ease-in-out';
     const sizeStyles = {
         sm: 'px-2.5 py-1 text-xs',
         md: 'px-4 py-1.5 text-sm',
         lg: 'px-5 py-2 text-base',
     };
      const variantStyles = {
        default: `border-transparent text-purple-400 hover:text-purple-300 hover:underline focus-visible:underline ${disabled || loading ? 'text-gray-600 hover:text-gray-600' : ''}`,
        primary: `border-transparent bg-purple-600 text-white hover:bg-purple-500 focus-visible:bg-purple-700 ${disabled || loading ? 'bg-gray-600 text-gray-400 hover:bg-gray-600' : ''}`,
        secondary: `border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 focus-visible:border-gray-500 ${disabled || loading ? 'border-gray-700 bg-gray-800 text-gray-500 hover:bg-gray-800' : ''}`,
        danger: `border-transparent bg-red-600 text-white hover:bg-red-500 focus-visible:bg-red-700 ${disabled || loading ? 'bg-red-800 text-red-300 hover:bg-red-800' : ''}`,
        warning: `border-transparent bg-yellow-500 text-black hover:bg-yellow-400 focus-visible:bg-yellow-600 ${disabled || loading ? 'bg-yellow-800/50 text-yellow-300 hover:bg-yellow-800/50' : ''}`,
        ghost: `border-transparent bg-transparent text-gray-400 hover:bg-gray-800/50 hover:text-gray-200 focus-visible:bg-gray-700/50 ${disabled || loading ? 'text-gray-600 hover:bg-transparent hover:text-gray-600' : ''}`,
    };

    const iconSize = size === 'sm' ? 14 : size === 'lg' ? 18 : 16;
    const isDisabled = disabled || loading;

    return (
        <div
            ref={ref}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            onClick={!isDisabled ? onClick : undefined}
            onKeyDown={(e) => { if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }}
            className={`${baseStyle} ${sizeStyles[size]} ${variantStyles[variant]} ${isDisabled ? 'opacity-70 cursor-not-allowed pointer-events-none' : 'cursor-pointer'} ${className}`}
            aria-disabled={isDisabled}
            title={title || (typeof children === 'string' ? children : '')}
            {...props}
        >
            {loading ? (
                 <Loader2 size={iconSize} className="animate-spin" aria-label="Loading..." />
            ) : (
                 <>
                    {Icon && iconPosition === 'left' && <Icon size={iconSize} aria-hidden="true" />}
                    {children}
                    {Icon && iconPosition === 'right' && <Icon size={iconSize} aria-hidden="true" />}
                </>
            )}
        </div>
    );
});
ClickableDiv.displayName = 'ClickableDiv';


// --- Tooltip Component ---
const Tooltip = ({ text, children, className }) => (
    <div className={`relative inline-block group ${className}`}>
        {children}
        <div className="absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none shadow-lg border border-gray-700/50">
            {text}
            <svg className="absolute left-1/2 top-full h-2 w-full -translate-x-1/2 text-gray-800" x="0px" y="0px" viewBox="0 0 255 255" xmlSpace="preserve"><polygon className="fill-current" points="0,0 127.5,127.5 255,0"/></svg>
        </div>
    </div>
);


// --- Sidebar Nav Item Component ---
const SidebarNavItem = ({ icon: Icon, text, href, active, onClick }) => (
    <ClickableLink
        href={href}
        onClick={onClick}
        variant='ghost'
        size='sm'
        className="!flex !items-center !pl-3 !pr-2 !py-1.5 !rounded-md !text-xs !font-medium !group !relative !text-gray-400 hover:!text-white hover:!bg-gray-800/50 !w-full !justify-start !border-none !shadow-none"
    >
        <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 text-gray-500 group-hover:text-gray-300`} strokeWidth={1.5} />
        <span className="truncate">{text}</span>
    </ClickableLink>
);

// --- Sidebar Component ---
const Sidebar = () => {
    const navigate = useNavigate();
    const { user, isLoaded } = useUser();
    const handleBackToDashboardClick = (e) => { e.preventDefault(); navigate('/echidna/efoundry/dashboard'); };
    const userEmail = isLoaded && user ? user.primaryEmailAddress?.emailAddress : 'Loading...';
    return (
        <div className="w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
            <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
                 <ClickableLink href="/echidna/efoundry/dashboard" onClick={handleBackToDashboardClick} variant='ghost' className="!flex !items-center group !border-none !p-0" title="Back to Echidna Foundry Dashboard">
                    <svg className="w-6 h-6 mr-1.5 group-hover:opacity-80 transition-opacity" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/></svg>
                    <span className="text-base font-bold text-white group-hover:text-gray-300 transition-colors">Echidna Foundry</span>
                 </ClickableLink>
            </div>
            <div className="flex-grow py-2"><nav className="space-y-0.5 px-2 mt-3"><SidebarNavItem key="back" icon={LayoutDashboard} text="Back to Dashboard" href="/echidna/efoundry/dashboard" onClick={handleBackToDashboardClick} /></nav></div>
            <div className="p-2 border-t border-gray-800/50 mt-auto">
                <ClickableLink href="#profile" title={userEmail} variant='ghost' size='sm' className="!flex !items-center !p-2 !rounded-md !text-xs !text-gray-400 hover:!bg-gray-800/50 group !w-full !justify-start !border-none">
                    <Mail className="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-300"/>
                    <span className="truncate font-medium">{userEmail}</span>
                </ClickableLink>
            </div>
        </div>
    );
};

// --- Top Bar Component ---
const TopBar = ({ pageTitle }) => {
     const { user } = useUser();
     const userInitial = user?.firstName?.[0] || user?.username?.[0] || user?.primaryEmailAddress?.emailAddress?.[0] || '?';
    return (
        <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20">
             <div className="flex items-center space-x-3"><span className="text-sm font-medium text-white">{pageTitle}</span></div>
             <div className="flex items-center space-x-2 md:space-x-3">
                 <div className="relative hidden md:block"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"/><input type="search" placeholder="Search..." className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 text-xs focus:ring-1 focus:ring-purple-500 focus:border-purple-500 outline-none" /></div>
                 <Tooltip text="Current AWS Region (mock)">
                    <ClickableDiv variant='ghost' size='sm' className="text-xs text-gray-400 !rounded-sm !px-1 !border-none" title="Current AWS Region (mock)"><Globe className="w-4 h-4 mr-1" />us-east-1</ClickableDiv>
                 </Tooltip>
                 <Tooltip text="Model Playground">
                     <ClickableLink href={MODEL_PLAYGROUND_URL} target="_blank" variant='ghost' size='sm' className="!px-1 !border-none"><Cpu className="w-5 h-5 text-gray-400 hover:text-purple-400" /></ClickableLink>
                 </Tooltip>
                 <Tooltip text="Help">
                     <ClickableLink href="#help" variant='ghost' size='sm' className="!px-1 !border-none"><LifeBuoy className="w-5 h-5 text-gray-400 hover:text-purple-400" /></ClickableLink>
                 </Tooltip>
                 <Tooltip text="Notifications (mock)">
                     <ClickableDiv variant='ghost' size='sm' className="!px-1 !border-none"><Bell className="w-5 h-5 text-gray-400 hover:text-purple-400" /></ClickableDiv>
                 </Tooltip>
                  <Tooltip text="Account Options (mock)">
                    <ClickableDiv variant='ghost' size='sm' className="flex items-center space-x-1 !p-1 rounded !border-none" title="Account Options (mock)">
                        <span className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center text-[10px] font-bold text-white">{userInitial}</span>
                        <ChevronDown className="w-4 h-4 text-gray-500"/>
                    </ClickableDiv>
                 </Tooltip>
             </div>
          </div>
    );
};

// --- AWS Style Input Component ---
const AwsStyleInput = React.memo(({
    label, description, placeholder, value, onChange, type = 'text', maxLength, rows = 4, required = false, id,
    disabled = false
}) => (
    <div className="sm:col-span-full">
        <label htmlFor={id} className="block text-sm font-medium leading-6 text-gray-300">
            {label} {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {description && <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p>}
        <div className="mt-2">
            {type === 'textarea' ? (
                <div className="relative">
                    <textarea id={id} name={id} value={value ?? ''} onChange={onChange} maxLength={maxLength} rows={rows} required={required} disabled={disabled}
                        className={`block w-full rounded-md border-0 py-1.5 px-3 bg-gray-900 text-gray-200 shadow-sm ring-1 ring-inset ring-gray-700 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6 resize-y scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                        placeholder={placeholder} />
                     {maxLength && (<span className="absolute bottom-2 right-2 text-[10px] text-gray-500 pointer-events-none">{(value?.length || 0)}/{maxLength}</span>)}
                </div>
            ) : ( <input type={type} id={id} name={id} value={value ?? ''} onChange={onChange} maxLength={maxLength} required={required} disabled={disabled}
                    className={`block w-full rounded-md border-0 py-1.5 px-3 bg-gray-900 text-gray-200 shadow-sm ring-1 ring-inset ring-gray-700 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    placeholder={placeholder} /> )}
        </div>
    </div>
));
AwsStyleInput.displayName = 'AwsStyleInput';

// --- Custom Node Type for Datasets ---
const DatasetNode = React.memo(({ data, selected }) => {
    const isParent = data.isParent;
    const baseHandleClass = "!w-2.5 !h-2.5 !border !border-gray-600 !rounded-sm !transition-all";
    const sourceHandleClass = `${baseHandleClass} !bg-gray-600 hover:!bg-purple-500 hover:!border-purple-400`;
    const targetHandleClass = `${baseHandleClass} !bg-gray-600 ${isParent ? 'hover:!bg-blue-500 hover:!border-blue-400' : 'hover:!bg-purple-500 hover:!border-purple-400'}`;
    const nonConnectableHandleClass = `${baseHandleClass} !bg-gray-800 !border-gray-700 !opacity-50 !cursor-not-allowed`;

    const getHandleProps = (type, position) => {
        const props = { type, position };
        if (type === 'source') {
            props.isConnectable = !isParent; // Source only if NOT parent
            props.className = isParent ? nonConnectableHandleClass : sourceHandleClass;
        } else { // type === 'target'
            props.isConnectable = true; // Target always potentially connectable
            props.className = targetHandleClass;
        }
        // Add position-specific offsets
        if (position === Position.Top) props.className += ' !-mt-[5px]';
        if (position === Position.Bottom) props.className += ' !-mb-[5px]';
        if (position === Position.Left) props.className += ' !-ml-[5px]';
        if (position === Position.Right) props.className += ' !-mr-[5px]';
        return props;
    };

    // Calculate stats if needed (fallback)
    const sectionCount = data.originalData?.sections?.length || 0;
    const totalInputTokens = data.originalData?.totalInputTokens || 0;

    return (
        <div className={`
            p-3 rounded-lg shadow-md w-48 min-h-[60px] transition-all duration-150 relative group
            border ${selected ? 'ring-2 ring-offset-1 ring-offset-black ring-blue-500' : ''}
            ${isParent ? 'bg-gradient-to-br from-purple-800 to-purple-900 border-purple-600 text-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'}
            ${isParent ? 'cursor-default' : 'cursor-grab'}
        `} data-isparent={isParent}> {/* Add data attribute for easier selection */}
            {/* Handles on all sides */}
            <Handle id={`${Position.Top}-target`} {...getHandleProps('target', Position.Top)} />
            <Handle id={`${Position.Top}-source`} {...getHandleProps('source', Position.Top)} />
            <Handle id={`${Position.Left}-target`} {...getHandleProps('target', Position.Left)} />
            <Handle id={`${Position.Left}-source`} {...getHandleProps('source', Position.Left)} />
            <Handle id={`${Position.Right}-target`} {...getHandleProps('target', Position.Right)} />
            <Handle id={`${Position.Right}-source`} {...getHandleProps('source', Position.Right)} />
            <Handle id={`${Position.Bottom}-target`} {...getHandleProps('target', Position.Bottom)} />
            <Handle id={`${Position.Bottom}-source`} {...getHandleProps('source', Position.Bottom)} />

            {/* Node Content */}
            <div className="flex items-center gap-2 mb-1">
                <Database size={14} className={isParent ? "text-purple-300" : "text-gray-500"}/>
                <span className="text-xs font-semibold truncate flex-grow" title={data.label}>{data.label}</span>
            </div>
            {/* Display basic size info from calculated/fetched stats */}
             <div className="text-[10px] text-gray-400 mt-1 flex flex-col gap-0.5">
                 {sectionCount > 0 && (
                     <div className="flex items-center gap-1" title="Number of Sections/Chunks">
                          <FileText size={10} /> {sectionCount.toLocaleString()} sections
                      </div>
                  )}
                  {totalInputTokens > 0 && (
                      <div className="flex items-center gap-1" title="Estimated Preprocessed Tokens">
                          <Binary size={10} /> {totalInputTokens.toLocaleString()} tokens
                      </div>
                  )}
            </div>

            {/* Parent Badge */}
            {isParent && (
                <div className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-purple-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider shadow-lg">
                    Parent
                </div>
            )}
        </div>
    );
});
DatasetNode.displayName = 'DatasetNode';


// --- Custom Node Type for Agents ---
const AgentNode = React.memo(({ data, selected }) => {
    const isCurrentAgent = data.isParent; // Use isParent to mark the agent being created
    const baseHandleClass = "!w-2.5 !h-2.5 !border !border-gray-600 !rounded-sm !transition-all";
    const sourceHandleClass = `${baseHandleClass} !bg-gray-600 hover:!bg-blue-500 hover:!border-blue-400`;
    const targetHandleClass = `${baseHandleClass} !bg-gray-600 ${isCurrentAgent ? 'hover:!bg-green-500 hover:!border-green-400' : 'hover:!bg-blue-500 hover:!border-blue-400'}`;
    const nonConnectableHandleClass = `${baseHandleClass} !bg-gray-800 !border-gray-700 !opacity-50 !cursor-not-allowed`;

    const getHandleProps = (type, position) => {
        const props = { type, position };
        if (type === 'source') {
            props.isConnectable = !isCurrentAgent; // Source only if existing agent
            props.className = isCurrentAgent ? nonConnectableHandleClass : sourceHandleClass;
        } else { // type === 'target'
            props.isConnectable = true; // Target always potentially connectable
            props.className = targetHandleClass;
        }
         // Add position-specific offsets
        if (position === Position.Top) props.className += ' !-mt-[5px]';
        if (position === Position.Bottom) props.className += ' !-mb-[5px]';
        if (position === Position.Left) props.className += ' !-ml-[5px]';
        if (position === Position.Right) props.className += ' !-mr-[5px]';
        return props;
    };

    return (
        <div className={`
            p-3 rounded-lg shadow-md w-48 min-h-[60px] transition-all duration-150 relative group
            border ${selected ? 'ring-2 ring-offset-1 ring-offset-black ring-blue-500' : ''}
            ${isCurrentAgent ? 'bg-gradient-to-br from-blue-800 to-blue-900 border-blue-600 text-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'}
            ${ isCurrentAgent ? 'cursor-default' : 'cursor-grab' }
        `} data-isparent={isCurrentAgent}> {/* Add data attribute */}
            {/* Handles on all sides */}
            <Handle id={`${Position.Top}-target`} {...getHandleProps('target', Position.Top)} />
            <Handle id={`${Position.Top}-source`} {...getHandleProps('source', Position.Top)} />
            <Handle id={`${Position.Left}-target`} {...getHandleProps('target', Position.Left)} />
            <Handle id={`${Position.Left}-source`} {...getHandleProps('source', Position.Left)} />
            <Handle id={`${Position.Right}-target`} {...getHandleProps('target', Position.Right)} />
            <Handle id={`${Position.Right}-source`} {...getHandleProps('source', Position.Right)} />
            <Handle id={`${Position.Bottom}-target`} {...getHandleProps('target', Position.Bottom)} />
            <Handle id={`${Position.Bottom}-source`} {...getHandleProps('source', Position.Bottom)} />

            {/* Node Content */}
            <div className="flex items-center gap-2 mb-1">
                <Brain size={14} className={isCurrentAgent ? "text-blue-300" : "text-gray-500"}/>
                <span className="text-xs font-semibold truncate flex-grow" title={data.label}>{data.label}</span>
            </div>

            {/* Parent Badge (Current Agent) */}
            {isCurrentAgent && (
                <div className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider shadow-lg">
                    Current
                </div>
            )}
        </div>
    );
});
AgentNode.displayName = 'AgentNode';


// --- Radio Button Group Component ---
const RadioGroup = ({ options, selectedValue, onChange, name, title, disabled = false }) => (
    <div>
        {title && <label className="block text-sm font-medium leading-6 text-gray-300 mb-1.5">{title}</label>}
        <div className="flex flex-wrap gap-x-4 gap-y-2">
            {options.map((option) => (
                <label key={option.value} className={`flex items-center gap-2 text-sm ${disabled ? 'cursor-not-allowed text-gray-600' : 'cursor-pointer text-gray-400 hover:text-gray-200'}`}>
                    <input
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={selectedValue === option.value}
                        onChange={() => !disabled && onChange(option.value)}
                        disabled={disabled}
                        className="sr-only" // Hide actual radio
                    />
                    {selectedValue === option.value
                        ? <CircleDot size={16} className={disabled ? "text-gray-600" : "text-purple-500"} />
                        : <Circle size={16} className="text-gray-600" />
                    }
                    {/* Allow complex labels */}
                    {option.label}
                </label>
            ))}
        </div>
    </div>
);

// --- Calculate Aggregate Dataset Stats Utility ---
const calculateAggregateStats = (datasets) => {
    if (!Array.isArray(datasets) || datasets.length === 0) {
        return { totalSectionCount: 0, totalChunkCount: 0, totalInputTokens: 0, avgTextLength: 0, totalTextLength: 0, names: [] };
    }

    let totalSectionCount = 0;
    let totalChunkCount = 0;
    let totalInputTokens = 0;
    let totalTextLength = 0;
    const names = [];

    datasets.forEach(dataset => {
        if (dataset) {
            names.push(dataset.name);
            const sections = Array.isArray(dataset.sections) ? dataset.sections : [];
            const sectionCount = sections.length;
            totalSectionCount += sectionCount;
            totalChunkCount += dataset.chunkCount || sectionCount; // Use backend chunkCount or fallback
            totalInputTokens += dataset.totalInputTokens || 0; // Sum backend tokens

            sections.forEach(section => {
                totalTextLength += section.processed_content?.length || 0;
            });
        }
    });

    const avgTextLength = totalSectionCount > 0 ? Math.round(totalTextLength / totalSectionCount) : 0;

    return {
        totalSectionCount,
        totalChunkCount,
        totalInputTokens, // Total preprocessed tokens across datasets
        avgTextLength,     // Average section length across all sections in the datasets
        totalTextLength,   // Total character length across all sections
        names              // List of dataset names included
    };
};


// --- Dataset Size Info Component (Handles Single or Multiple) ---
const DatasetSizeInfo = React.memo(({ datasets, mode, creationMode }) => {
    const stats = useMemo(() => calculateAggregateStats(datasets), [datasets]);

    // Don't show if using default data or no datasets selected/available
    if (creationMode === 'default' || !datasets || datasets.length === 0) return null;

    const isHierarchy = mode === 'hierarchy' && datasets.length > 1;
    const title = isHierarchy ? `Aggregated Stats (${datasets.length} Datasets):` : 'Selected Dataset Stats:';
    const datasetNameDisplay = isHierarchy
        ? <Tooltip text={stats.names.join(', ')} className='inline-block'><span className='italic underline decoration-dotted cursor-help'>{datasets.length} datasets</span></Tooltip>
        : <span className="font-medium text-purple-400">{datasets[0]?.name || 'N/A'}</span>;


    return (
        <div className="mt-3 p-3 bg-gray-800/50 border border-gray-700/50 rounded-md text-xs text-gray-400 space-y-1.5">
            <h4 className="font-medium text-gray-300 mb-1 flex items-center gap-1.5">
                {isHierarchy && <Users size={14}/>} {title} {isHierarchy && datasetNameDisplay}
            </h4>
            <div className="flex items-center gap-2"><FileText size={12} /> Total Sections: <span className="font-medium text-gray-200">{stats.totalSectionCount?.toLocaleString() || 'N/A'}</span></div>
            <div className="flex items-center gap-2"><Puzzle size={12} /> Total Chunks: <span className="font-medium text-gray-200">{stats.totalChunkCount?.toLocaleString() || 'N/A'}</span></div>
            <div className="flex items-center gap-2"><Binary size={12} /> Total Preprocessed Tokens: <span className="font-medium text-gray-200">{stats.totalInputTokens?.toLocaleString() || 'N/A'}</span></div>
            <div className="flex items-center gap-2"><Gauge size={12} /> Avg. Section Length: <span className="font-medium text-gray-200">~{(stats.avgTextLength || 0).toLocaleString()} chars</span></div>
        </div>
    );
});
DatasetSizeInfo.displayName = 'DatasetSizeInfo';


// --- Dataset Content Viewer Component ---
const DatasetContentViewer = React.memo(({ dataset }) => {
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        // Reset expanded state when dataset changes
        setExpandedSections({});
    }, [dataset]);

    if (!dataset) {
        return <div className="text-sm text-gray-500 italic">Select a dataset to view its content.</div>;
    }
    if (!dataset.sections || dataset.sections.length === 0) {
        return <div className="text-sm text-yellow-500">Selected dataset ('{dataset.name}') has no processed sections.</div>;
    }

    const toggleSection = (index) => {
        setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
    };

    return (
        <div className="space-y-3">
            <h3 className="text-base font-semibold text-gray-200 border-b border-gray-700/50 pb-2 mb-3">
                {/* Show only Parent dataset content for simplicity in hierarchy */}
                Dataset Content: <span className="font-medium text-purple-400">{dataset.name}</span> ({dataset.sections.length} Sections)
            </h3>
            <div className="max-h-[600px] overflow-y-auto space-y-3 pr-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                {dataset.sections.map((section, index) => (
                    <div key={index} className="bg-gray-800/60 border border-gray-700/50 rounded-md">
                        <div
                            className="p-3 flex justify-between items-center cursor-pointer hover:bg-gray-700/40 transition-colors"
                            onClick={() => toggleSection(index)}
                        >
                            <div className='flex-grow overflow-hidden'> {/* Added overflow-hidden */}
                                <span className="text-sm font-medium text-gray-300 block truncate" title={section.topic || `Section ${index + 1}`}>
                                    {section.topic || `Section ${index + 1}`}
                                </span>
                                <span className="text-xs text-gray-400 block truncate mt-0.5" title={section.summary}>
                                    Summary: {section.summary || 'N/A'}
                                </span>
                            </div>
                            <div className='flex-shrink-0 ml-3'>
                                {expandedSections[index] ? <MinusSquare size={16} className="text-gray-500" /> : <PlusSquare size={16} className="text-gray-500" />}
                            </div>
                        </div>
                        {expandedSections[index] && (
                            <div className="border-t border-gray-700/50 p-3">
                                <h4 className="text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Processed Content:</h4>
                                <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words bg-black/30 p-2 rounded font-mono max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800/50">
                                    <code>{section.processed_content || '(No content)'}</code>
                                </pre>
                                {section.keywords && section.keywords.length > 0 && (
                                    <div className="mt-2">
                                        <h4 className="text-xs font-semibold text-gray-400 mb-1 uppercase tracking-wider">Keywords:</h4>
                                        <div className="flex flex-wrap gap-1">
                                            {section.keywords.map((kw, kwIndex) => (
                                                <span key={kwIndex} className="text-[10px] bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded-full">{kw}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
});
DatasetContentViewer.displayName = 'DatasetContentViewer';

// --- Register Custom Node Types ---
const nodeTypes = { dataset: DatasetNode, agent: AgentNode };

// --- Constants ---
const CURRENT_AGENT_NODE_ID = "__CURRENT_AGENT__";
// const API_BASE_URL = 'https://web-production-5ee8.up.railway.app/echidna'; // LOCAL DEV
const API_BASE_URL = 'https://web-production-5ee8.up.railway.app/echidna'; // PRODUCTION
const FETCH_PREPROS_URL = `${API_BASE_URL}/fetchprepros`; // Fetch full datasets
const ADD_AGENT_URL = `${API_BASE_URL}/addagent`;
const FETCH_AGENTS_URL = `${API_BASE_URL}/myagents`;
const TESTER_BASE_URL = 'https://metabees.org';
const MODEL_PLAYGROUND_URL = '/echidna/efoundry/models';

// --- AI Model Definitions ---
const AI_MODELS = {
    'echidna-v4o': {
        id: 'echidna-v4o',
        name: 'Echidna v4o',
        pricing: { input: 0.01, output: 0.4 }, // USD per million tokens
        description: 'Latest flagship model, powerful & cost-effective for input.',
    },
    'echidna-v5-turbo': {
        id: 'echidna-v5-turbo',
        name: 'Echidna v5-Turbo',
        pricing: { input: 0.3, output: 0.5 }, // USD per million tokens
        description: 'Fast and capable model, balanced performance.',
    },
};
const DEFAULT_MODEL_ID = 'echidna-v4o';

// --- Main Echpilot Component ---
const Echpilot = () => {
  const pageTitle = 'Create New AI Agent';
  const navigate = useNavigate();
  const { user } = useUser();

  // --- Creation Mode State ---
  const [creationMode, setCreationMode] = useState('initial'); // 'initial', 'scratch', 'clone_pending', 'clone_select', 'default'
  const [clonableAgents, setClonableAgents] = useState([]);
  const [selectedAgentToCloneId, setSelectedAgentToCloneId] = useState('');
  const [isClonableAgentsLoading, setIsClonableAgentsLoading] = useState(false);
  const [clonableAgentsError, setClonableAgentsError] = useState(null);
  const [showDefaultDataWarning, setShowDefaultDataWarning] = useState(false);

  // --- Core Agent State ---
  const [agentName, setAgentName] = useState('');
  const [agentPurpose, setAgentPurpose] = useState('');
  const [keyResponsibilities, setKeyResponsibilities] = useState('');
  const [communicationStyle, setCommunicationStyle] = useState('');
  const [constraintsAndEthics, setConstraintsAndEthics] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [selectedModelId, setSelectedModelId] = useState(DEFAULT_MODEL_ID);

  // --- Dataset State ---
  const [datasetMode, setDatasetMode] = useState('single'); // 'single', 'hierarchy'
  const [allDatasets, setAllDatasets] = useState([]);
  const [selectedSingleDatasetId, setSelectedSingleDatasetId] = useState('');
  const [isDatasetsLoading, setIsDatasetsLoading] = useState(false);
  const [datasetError, setDatasetError] = useState(null);
  const [datasetNodes, setDatasetNodes, onDatasetNodesChange] = useNodesState([]);
  const [datasetEdges, setDatasetEdges, onDatasetEdgesChange] = useEdgesState([]);
  const [selectedNodeForDatasetParent, setSelectedNodeForDatasetParent] = useState(null);
  const [selectedDatasetParentId, setSelectedDatasetParentId] = useState(null);
  const datasetFlowWrapper = useRef(null);
  const [datasetRfInstance, setDatasetRfInstance] = useState(null);

  // --- Agent Collaboration State ---
  const [agentMode, setAgentMode] = useState('single'); // 'single', 'swarm'
  const [existingAgents, setExistingAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [agentFetchError, setAgentFetchError] = useState(null);
  const [agentNodes, setAgentNodes, onAgentNodesChange] = useNodesState([]);
  const [agentEdges, setAgentEdges, onAgentEdgesChange] = useEdgesState([]);
  const agentFlowWrapper = useRef(null);
  const [agentRfInstance, setAgentRfInstance] = useState(null);

  // --- Security Addon State ---
  const [firewallEnabled, setFirewallEnabled] = useState(false);

  // --- UI State ---
  const [showDatasetContent, setShowDatasetContent] = useState(false);

  // --- Submission & Result State ---
  const [generatedScript, setGeneratedScript] = useState('');
  const [generatedUrls, setGeneratedUrls] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [lastCopiedText, setLastCopiedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showAgentLinkInfo, setShowAgentLinkInfo] = useState(false);
  const [estimatedCostPerChat, setEstimatedCostPerChat] = useState(null);

  // --- Handlers ---
  const handleInputChange = (setter) => (event) => { setter(event.target.value); };
  const handleCopy = useCallback((textToCopy) => {
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy).then(() => {
        setLastCopiedText(textToCopy); setIsCopied(true);
        setTimeout(() => { setLastCopiedText(prev => { if (prev === textToCopy) { setIsCopied(false); return ''; } return prev; }); }, 2000);
    }).catch(err => { console.error("Copy failed:", err); setError('Failed to copy.'); });
  }, []);
  const handleToggleSecretKey = useCallback(() => setShowSecret(prev => !prev), []);
  const handleShowAgentLinkInfoPopup = useCallback(() => setShowAgentLinkInfo(true), []);
  const handleCloseAgentLinkInfoPopup = useCallback(() => setShowAgentLinkInfo(false), []);
  const toggleShowDatasetContent = useCallback(() => setShowDatasetContent(prev => !prev), []);

   // --- Reset Form State ---
   const resetForm = useCallback(() => {
      setAgentName('');
      setAgentPurpose('');
      setKeyResponsibilities('');
      setCommunicationStyle('');
      setConstraintsAndEthics('');
      setLogoUrl('');
      setSelectedModelId(DEFAULT_MODEL_ID);
      setFirewallEnabled(false);
      setDatasetMode('single');
      setSelectedSingleDatasetId('');
      setSelectedDatasetParentId(null);
      setSelectedNodeForDatasetParent(null); // Also reset the temporary selection
      // Resetting graph state can be optional, depends on desired UX
      // setDatasetNodes([]); // Consider if keeping nodes is better
      // setDatasetEdges([]);
      setAgentMode('single');
      // setAgentNodes([]); // Consider if keeping nodes is better
      // setAgentEdges([]);
      setError(null);
      setGeneratedUrls(null);
      setGeneratedScript('');
      setSecret('');
      setShowSecret(false);
      setShowDatasetContent(false);
      setEstimatedCostPerChat(null);
   }, []); // Add setters if they were directly dependencies, but seems okay

  // --- Fetch Full Datasets ---
  const fetchAndSetDatasets = useCallback(async () => {
    if (!user?.id) { setDatasetError("Login required to fetch datasets."); return; }
    setIsDatasetsLoading(true); setDatasetError(null);
    setAllDatasets([]); setDatasetNodes([]);
    setSelectedDatasetParentId(null); setSelectedNodeForDatasetParent(null);
    setSelectedSingleDatasetId('');
    setShowDatasetContent(false); // Hide content viewer when reloading
    try {
        const response = await axios.post(FETCH_PREPROS_URL, { cid: user.id });
        if (response.data && Array.isArray(response.data.preprocessedDatasets)) {
            const fetchedDatasets = response.data.preprocessedDatasets.filter(d => d?._id && d?.name);
            setAllDatasets(fetchedDatasets);
            // Reset nodes with new data
            setDatasetNodes(fetchedDatasets.map((d, i) => ({
                id: d._id, type: 'dataset',
                data: { label: d.name, isParent: false, originalData: d },
                position: { x: (i % 4) * 240 + Math.random()*10, y: Math.floor(i / 4) * 120 + Math.random()*10 },
                selectable: true, draggable: true,
            })));
            setDatasetEdges([]); // Clear edges when reloading datasets
            if (fetchedDatasets.length === 0) {
               setDatasetError("No datasets found. Please create or preprocess data first.");
            }
        } else { setDatasetError("Invalid dataset format received from server."); }
    } catch (err) { setDatasetError(err.response?.data?.message || err.message || 'Failed to fetch datasets.');
    } finally { setIsDatasetsLoading(false); }
  }, [user?.id, setDatasetNodes, setDatasetEdges]); // Keep setters

  // --- Fetch Existing Agents ---
  const fetchAgents = useCallback(async (purpose = 'collaboration') => {
    if (!user?.id) return;
    if (purpose === 'cloning') {
        setIsClonableAgentsLoading(true);
        setClonableAgentsError(null);
        setClonableAgents([]);
        setSelectedAgentToCloneId(''); // Reset selection when refetching
    } else { // 'collaboration'
        setIsLoadingAgents(true);
        setAgentFetchError(null);
        setExistingAgents([]);
        // Optionally reset agent graph state if desired when fetching for collaboration
        // setAgentNodes([]);
        // setAgentEdges([]);
    }

    try {
      const response = await axios.post(FETCH_AGENTS_URL, { cid: user.id });
      if (response.data.success && Array.isArray(response.data.agents)) {
        const validAgents = response.data.agents.filter(agent => agent && agent._id && agent.agentName);
        if (purpose === 'cloning') {
            setClonableAgents(validAgents || []);
            if (validAgents.length > 0) {
                setCreationMode('clone_select'); // Only go to select if agents exist
            } else {
                setClonableAgentsError("No existing agents found to clone.");
                setCreationMode('initial'); // Go back if no agents found
            }
        } else { // collaboration
            setExistingAgents(validAgents || []);
            // Update nodes immediately if in swarm mode
            if (agentMode === 'swarm') {
                const currentAgentLabel = agentName.trim() || 'New Agent';
                 const newAgentNodes = [
                    { id: CURRENT_AGENT_NODE_ID, type: 'agent', data: { label: currentAgentLabel, isParent: true }, position: { x: 350, y: 50 }, selectable: false, draggable: false, },
                    ...validAgents.map((agent, i) => ({ id: agent._id, type: 'agent', data: { label: agent.agentName, isParent: false, originalData: agent }, position: { x: (i % 3) * 240 + Math.random()*10, y: 200 + Math.floor(i / 3) * 120 + Math.random()*10 }, selectable: true, draggable: true, })),
                ];
                setAgentNodes(newAgentNodes);
                // Don't clear edges here if we want them persisted across fetches
                // setAgentEdges([]);
            }
        }
      } else {
        const errorMsg = response.data.error || response.data.message || 'Failed to fetch existing agents or invalid format';
        if (purpose === 'cloning') {
            setClonableAgentsError(errorMsg);
            setCreationMode('initial'); // Go back on error
        } else {
            setAgentFetchError(errorMsg);
        }
        if (response.status === 404 && purpose === 'cloning') {
            setClonableAgentsError("No existing agents found to clone.");
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Network error: Unable to fetch agents';
       if (err.response?.status === 404 && purpose === 'cloning') {
            setClonableAgentsError("No existing agents found to clone.");
       } else if (purpose === 'cloning') {
           setClonableAgentsError(errorMsg);
       } else {
           setAgentFetchError(errorMsg);
       }
       if (purpose === 'cloning') setCreationMode('initial'); // Go back on error
    } finally {
      if (purpose === 'cloning') {
          setIsClonableAgentsLoading(false);
      } else {
          setIsLoadingAgents(false);
      }
    }
   // Include state setters and other relevant dependencies
  }, [user?.id, setAgentNodes, setAgentEdges, setClonableAgents, setCreationMode, setIsClonableAgentsLoading, setClonableAgentsError,
      setIsLoadingAgents, setAgentFetchError, setExistingAgents, agentMode, agentName]);


   // Fetch initial data on mount
   useEffect(() => {
       if(user?.id) {
           // Fetch datasets first as they are needed for both scratch and clone paths potentially
           fetchAndSetDatasets();
           // Fetch agents for the collaboration section, not necessarily for cloning yet
           fetchAgents('collaboration');
       }
       // Intentionally only run on user ID change (and initial mount)
       // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [user?.id]); // Keep fetchAndSetDatasets, fetchAgents out to avoid loops


    // --- Handlers for Initial Creation Choice ---
    const handleStartScratch = useCallback(() => {
        resetForm();
        // Ensure datasets and agents are loaded/reloaded if needed
        if (allDatasets.length === 0 && !isDatasetsLoading) fetchAndSetDatasets();
        if (existingAgents.length === 0 && !isLoadingAgents) fetchAgents('collaboration');
        setCreationMode('scratch');
        window.scrollTo(0, 0);
    }, [resetForm, fetchAndSetDatasets, fetchAgents, allDatasets.length, existingAgents.length, isDatasetsLoading, isLoadingAgents]);

    const handleStartClone = useCallback(() => {
        resetForm(); // Reset form before fetching clones
        setCreationMode('clone_pending');
        fetchAgents('cloning'); // Fetch specifically for cloning
    }, [resetForm, fetchAgents]);

    const handleStartDefault = useCallback(() => {
        setShowDefaultDataWarning(true);
    }, []);

    const confirmUseDefaultData = useCallback(() => {
        resetForm();
        // Fetch agents for collaboration graph in default mode too, if not already loaded
        if (existingAgents.length === 0 && !isLoadingAgents) fetchAgents('collaboration');
        setCreationMode('default');
        setShowDefaultDataWarning(false);
        window.scrollTo(0, 0);
    }, [resetForm, fetchAgents, existingAgents.length, isLoadingAgents]);

    const cancelUseDefaultData = useCallback(() => {
        setShowDefaultDataWarning(false);
    }, []);


    // --- Handle Agent Cloning Confirmation ---
    // *** FIXED AREA ***
    const handleCloneAgentConfirm = useCallback(() => {
        if (!selectedAgentToCloneId) {
             setClonableAgentsError("No agent selected.");
             return;
        }
        // Find the agent object from the state populated by fetchAgents('cloning')
        const agentToClone = clonableAgents.find(a => a._id === selectedAgentToCloneId);

        if (agentToClone) {
            console.log("Cloning agent:", agentToClone); // Debug log

            // 1. Set cloned fields directly using state setters
            // Use empty string as fallback for null/undefined fields from the source agent
            setAgentPurpose(agentToClone.agentDetails?.purpose || '');
            setKeyResponsibilities(agentToClone.agentDetails?.responsibilities || '');
            setCommunicationStyle(agentToClone.agentDetails?.communicationStyle || '');
            setConstraintsAndEthics(agentToClone.agentDetails?.constraintsAndEthics || '');
            setLogoUrl(agentToClone.companyLogo || agentToClone.logoUrl || '');
            setSelectedModelId(agentToClone.modelId || DEFAULT_MODEL_ID);

            // 2. Reset fields that SHOULD NOT be cloned (or need user input)
            setAgentName(''); // Clear name, requires a new one
            setFirewallEnabled(false); // Reset firewall setting
            setDatasetMode('single'); // Reset dataset config to default
            setSelectedSingleDatasetId('');
            setSelectedDatasetParentId(null);
            setSelectedNodeForDatasetParent(null);
            setDatasetEdges([]); // Clear dataset connections
            setAgentMode('single'); // Reset agent connections to default
            setAgentEdges([]); // Clear agent connections

            // 3. Clear any previous errors or results
            setError(null);
            setGeneratedUrls(null);
            setGeneratedScript('');
            setSecret('');

            // 4. Ensure necessary supporting data (datasets, agents for collaboration) is available
            // These should ideally already be loaded or fetched by `handleStartScratch` dependencies if needed
            // Adding checks here provides redundancy.
            if (allDatasets.length === 0 && !isDatasetsLoading) {
                 console.log("Fetching datasets because they were missing during clone confirm.");
                 fetchAndSetDatasets();
             }
            if (existingAgents.length === 0 && !isLoadingAgents) {
                 console.log("Fetching collaboration agents because they were missing during clone confirm.");
                 fetchAgents('collaboration');
            }

            // 5. Switch mode to show the main form ('scratch' mode is used for the form)
            setCreationMode('scratch');

            // 6. Clear cloning-specific state (optional, but good practice)
            // setSelectedAgentToCloneId(''); // Keep selected ID cleared if needed, or reset later
            // setClonableAgents([]); // Don't clear clonableAgents here, might want to go back
            setClonableAgentsError(null); // Clear any error message from cloning selection

            // 7. Scroll to top for the user to start editing the cloned data
            window.scrollTo(0, 0);

        } else {
            // This case should be rare if the dropdown is populated correctly
            console.error("Could not find agent with ID:", selectedAgentToCloneId, "in list:", clonableAgents);
            setClonableAgentsError("Selected agent data not found. Please try selecting again.");
            // Stay on selection screen if agent data isn't found
            setCreationMode('clone_select');
        }
    // Add ALL dependencies used inside the function:
    // States read: selectedAgentToCloneId, clonableAgents, allDatasets, existingAgents, isDatasetsLoading, isLoadingAgents
    // State setters: setAgentPurpose, setKeyResponsibilities, setCommunicationStyle, setConstraintsAndEthics,
    //                setLogoUrl, setSelectedModelId, setAgentName, setFirewallEnabled, setDatasetMode,
    //                setSelectedSingleDatasetId, setSelectedDatasetParentId, setSelectedNodeForDatasetParent, setDatasetEdges,
    //                setAgentMode, setAgentEdges, setError, setGeneratedUrls, setGeneratedScript, setSecret,
    //                setCreationMode, setClonableAgentsError
    // Functions called: fetchAndSetDatasets, fetchAgents
    }, [
        selectedAgentToCloneId, clonableAgents, allDatasets, existingAgents, isDatasetsLoading, isLoadingAgents, // States read
        setAgentPurpose, setKeyResponsibilities, setCommunicationStyle, setConstraintsAndEthics, // State setters (cloned)
        setLogoUrl, setSelectedModelId, // State setters (cloned)
        setAgentName, setFirewallEnabled, setDatasetMode, setSelectedSingleDatasetId, // State setters (reset)
        setSelectedDatasetParentId, setSelectedNodeForDatasetParent, setDatasetEdges, // State setters (reset)
        setAgentMode, setAgentEdges, // State setters (reset)
        setError, setGeneratedUrls, setGeneratedScript, setSecret, // State setters (results/errors)
        setCreationMode, setClonableAgentsError, // State setters (mode/UI)
        fetchAndSetDatasets, fetchAgents // Functions called
    ]);


  // --- Get Currently Relevant Datasets ---
  const relevantDatasets = useMemo(() => {
    if (creationMode === 'default' || !allDatasets || allDatasets.length === 0) return [];
    if (datasetMode === 'single') {
        const single = allDatasets.find(d => d._id === selectedSingleDatasetId);
        return single ? [single] : [];
    } else if (datasetMode === 'hierarchy' && selectedDatasetParentId) {
        const parent = allDatasets.find(d => d._id === selectedDatasetParentId);
        if (!parent) return [];
        const subDatasetIds = datasetEdges
            .filter(edge => edge.target === selectedDatasetParentId && edge.source !== selectedDatasetParentId) // Ensure source isn't the parent itself
            .map(edge => edge.source);
        const subs = allDatasets.filter(d => subDatasetIds.includes(d._id));
        // Ensure parent is always first, followed by unique subs
        return [parent, ...subs.filter(sub => sub._id !== parent._id)];
    }
    return [];
  }, [creationMode, datasetMode, selectedSingleDatasetId, selectedDatasetParentId, datasetEdges, allDatasets]);

  // --- Calculate Estimated Cost ---
  useEffect(() => {
    const calculateCost = () => {
        if (creationMode === 'default' || !relevantDatasets || relevantDatasets.length === 0 || !selectedModelId) {
            setEstimatedCostPerChat(null); return;
        }
        const model = AI_MODELS[selectedModelId];
        if (!model || !model.pricing) {
            setEstimatedCostPerChat(null); return;
        }
        const aggregateStats = calculateAggregateStats(relevantDatasets);
        const inputPrice = model.pricing.input / 1000000;
        const outputPrice = model.pricing.output / 1000000;
        const avgSectionsSelected = 5, avgResponseTokens = 250, promptOverheadTokens = 600, userMessageTokens = 50, charToTokenFactor = 4;
        // Prefer backend token count if available and seems reasonable, otherwise estimate from text length
        const selectionInputEstimateFromText = Math.round((aggregateStats.totalTextLength || 0) / charToTokenFactor);
        const selectionInputTokens = (aggregateStats.totalInputTokens > selectionInputEstimateFromText / 3) // Use backend if > 1/3 of simple estimate
                                     ? aggregateStats.totalInputTokens
                                     : selectionInputEstimateFromText;
        const selectionOutputTokens = 30; // Estimate for selection process output (e.g., IDs)
        const avgSectionTokens = Math.round((aggregateStats.avgTextLength || 0) / charToTokenFactor);
        const selectedContentTokens = avgSectionsSelected * avgSectionTokens;
        const responseInputTokens = selectedContentTokens + promptOverheadTokens + userMessageTokens;
        const responseOutputTokens = avgResponseTokens;
        const totalInput = selectionInputTokens + responseInputTokens;
        const totalOutput = selectionOutputTokens + responseOutputTokens;
        const cost = (totalInput * inputPrice) + (totalOutput * outputPrice);
        setEstimatedCostPerChat(cost);
    };
    calculateCost();
  }, [relevantDatasets, selectedModelId, datasetMode, creationMode]);


  // --- Update Dataset Nodes when parent or mode changes ---
  useEffect(() => {
    // Update node visual state (parent marker, draggability)
    setDatasetNodes((nds) => nds.map((node) => ({
        ...node,
        data: { ...node.data, isParent: node.id === selectedDatasetParentId },
        draggable: node.id !== selectedDatasetParentId, // Parent node is not draggable
        selectable: true, // All nodes remain selectable
    })));

    // Reset temporary selection state if a parent IS selected
    if (selectedDatasetParentId) {
        setSelectedNodeForDatasetParent(null);
    }

    // Clear hierarchy edges and parent if switching back to single mode
    if (datasetMode === 'single') {
        setSelectedDatasetParentId(null); // Clear parent ID
        setDatasetEdges([]); // Clear hierarchy edges
    }

    // Determine if content viewer should be shown (based on relevant data and not being default)
    const shouldShowContent = relevantDatasets.length > 0 && creationMode !== 'default';
    // Only update show/hide state if it needs to change
    setShowDatasetContent(prev => prev === shouldShowContent ? prev : shouldShowContent);

}, [selectedDatasetParentId, datasetMode, setDatasetNodes, setDatasetEdges, relevantDatasets.length, creationMode]); // Add missing dependencies


  // --- Update Agent Nodes when mode or existing agents change ---
   useEffect(() => {
       // Update nodes only if mode is 'swarm' and there are agents to display
       if (agentMode === 'swarm' && existingAgents.length > 0) {
           const currentAgentLabel = agentName.trim() || 'New Agent';
           const newAgentNodes = [
               // Current agent node (always present in swarm view)
               { id: CURRENT_AGENT_NODE_ID, type: 'agent', data: { label: currentAgentLabel, isParent: true }, position: { x: 350, y: 50 }, selectable: false, draggable: false, },
               // Existing agent nodes
               ...existingAgents.map((agent, i) => ({
                   id: agent._id,
                   type: 'agent',
                   data: { label: agent.agentName, isParent: false, originalData: agent },
                   position: { x: (i % 3) * 240 + Math.random()*5, y: 200 + Math.floor(i / 3) * 120 + Math.random()*5 }, // Slight random offset
                   selectable: true,
                   draggable: true,
                })),
           ];
           setAgentNodes(newAgentNodes);
           // Keep existing edges when agents reload unless explicitly cleared
           // setAgentEdges([]); // Optional: uncomment to clear edges on agent list refresh

           // Fit view after a short delay to allow rendering
           const timer = setTimeout(() => agentRfInstance?.fitView({ padding: 0.3, maxZoom: 1.2 }), 150);
           return () => clearTimeout(timer); // Cleanup timer

       } else if (agentMode !== 'swarm') {
           // If not in swarm mode, clear nodes and edges related to swarm view
           setAgentNodes([]);
           setAgentEdges([]);
       }
   // Dependencies: mode, agent list, agent name (for current node label), instance (for fitView)
  }, [agentMode, existingAgents, agentName, setAgentNodes, setAgentEdges, agentRfInstance]);


  // --- React Flow Dataset Handlers ---
  const onDatasetNodeClick = useCallback((event, node) => {
    // Only allow selecting a node for parenting if in hierarchy mode
    if (datasetMode === 'hierarchy') {
        setSelectedNodeForDatasetParent(node);
    } else {
        // Clear selection if not in hierarchy mode
        setSelectedNodeForDatasetParent(null);
    }
  }, [datasetMode]); // Dependency: datasetMode

  const handleSetDatasetParent = useCallback(() => {
    // Only set if a node is selected and it's different from the current parent
    if (selectedNodeForDatasetParent && selectedNodeForDatasetParent.id !== selectedDatasetParentId) {
      const newParentId = selectedNodeForDatasetParent.id;
      setSelectedDatasetParentId(newParentId);
      // Clear existing edges when a *new* parent is set
      setDatasetEdges([]);
      // Fit view after setting parent
      setTimeout(() => datasetRfInstance?.fitView({ padding: 0.3, maxZoom: 1.2 }), 100);
    }
  }, [selectedNodeForDatasetParent, selectedDatasetParentId, setSelectedDatasetParentId, setDatasetEdges, datasetRfInstance]); // Dependencies

  const isValidDatasetConnection = useCallback((connection) => {
    // Connections only valid in hierarchy mode, with a parent selected, and not in default creation mode
    if (creationMode === 'default' || datasetMode !== 'hierarchy' || !selectedDatasetParentId) return false;

    const reactFlowInstance = datasetRfInstance; // Use state variable
    if (!reactFlowInstance) return false; // Instance might not be ready

    const nodes = reactFlowInstance.getNodes();
    const targetNode = nodes.find(n => n.id === connection.target);
    const sourceNode = nodes.find(n => n.id === connection.source);

    // Basic checks: nodes exist, not connecting to self
    if (!targetNode || !sourceNode || sourceNode.id === targetNode.id) return false;

    // Valid connection: Source is NOT the parent, Target IS the parent
    return sourceNode.id !== selectedDatasetParentId && targetNode.id === selectedDatasetParentId;
  }, [selectedDatasetParentId, datasetRfInstance, datasetMode, creationMode]); // Dependencies


  const onDatasetConnect = useCallback((params) => {
    if (creationMode === 'default' || datasetMode !== 'hierarchy') return; // Only connect in hierarchy mode (not default)

    setDatasetEdges((eds) => addEdge({
        ...params,
        type: 'smoothstep', // Or 'default', 'step'
        markerEnd: { type: MarkerType.ArrowClosed, color: '#a855f7', width: 15, height: 15 },
        style: { strokeWidth: 1.5, stroke: '#a855f7' },
        animated: false // Connections usually aren't animated
    }, eds));
  }, [setDatasetEdges, creationMode, datasetMode]); // Dependencies


   // --- React Flow Agent Handlers ---
   const isValidAgentConnection = useCallback((connection) => {
       if (agentMode !== 'swarm') return false; // Only valid in swarm mode

       const reactFlowInstance = agentRfInstance; // Use state variable
        if (!reactFlowInstance) return false;

       const nodes = reactFlowInstance.getNodes();
       const targetNode = nodes.find(n => n.id === connection.target);
       const sourceNode = nodes.find(n => n.id === connection.source);

       // Basic checks
       if (!targetNode || !sourceNode || sourceNode.id === targetNode.id) return false;

       // Valid connection: Source is an EXISTING agent, Target IS the CURRENT agent being created
       return sourceNode.id !== CURRENT_AGENT_NODE_ID && targetNode.id === CURRENT_AGENT_NODE_ID;
  }, [agentRfInstance, agentMode]); // Dependencies

  const onAgentConnect = useCallback((params) => {
    if (agentMode !== 'swarm') return; // Only connect in swarm mode

    setAgentEdges((eds) => addEdge({
        ...params,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed, color: '#2563eb', width: 15, height: 15 },
        style: { strokeWidth: 1.5, stroke: '#2563eb' },
        animated: false
    }, eds));
  }, [setAgentEdges, agentMode]); // Dependencies

 // --- Form Submission ---
 const handleSubmit = useCallback(async (event) => {
    if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
    }
    setError(null); // Clear previous errors
    setIsLoading(true); // Set loading state

    const cid = user?.id;
    if (!cid) {
        setError("User not logged in or user ID is missing.");
        setIsLoading(false);
        return;
    }
    // Required field validation
    if (!agentName.trim() || !agentPurpose.trim() || !keyResponsibilities.trim() || !communicationStyle.trim() || !constraintsAndEthics.trim()) {
        setError("Please fill all required agent detail fields (*).");
        setIsLoading(false);
        // Scroll to the form top maybe?
        formRef.current?.scrollIntoView({ behavior: 'smooth' });
        return;
    }
    if (!selectedModelId || !AI_MODELS[selectedModelId]) {
        setError("Please select a valid AI model.");
        setIsLoading(false);
        return;
    }

    // --- Dataset Payload Preparation (Skip if in 'default' mode) ---
    let datasetPayload = null;
    if (creationMode !== 'default') {
        // Use the memoized 'relevantDatasets' which reflects the current valid selection/hierarchy
        const finalRelevantDatasets = relevantDatasets;

        // Validate dataset selection based on mode
        if (!finalRelevantDatasets || finalRelevantDatasets.length === 0) {
            if (datasetMode === 'single' && !selectedSingleDatasetId && allDatasets.length > 0) {
                setError("Please select a dataset or choose 'Use Default'.");
            } else if (datasetMode === 'hierarchy' && !selectedDatasetParentId && allDatasets.length > 0) {
                setError("Please select a dataset node and click 'Set as Parent' in the hierarchy view.");
            } else if (allDatasets.length === 0 && !isDatasetsLoading) {
                 setError("No datasets available. Please upload or process data first, or choose 'Use Default'.");
            } else {
                // Generic fallback if selection is somehow invalid despite UI state
                 setError("Dataset configuration is invalid. Please check selection or hierarchy parent.");
            }
             setIsLoading(false);
             return;
        }

        // Construct payload based on mode using relevantDatasets
        const parentDataset = finalRelevantDatasets[0]; // First item is always parent (or the single dataset)
        if (!parentDataset?._id) {
             setError("Could not determine the primary dataset ID. Please check your selection.");
             setIsLoading(false);
             return;
        }

        if (datasetMode === 'single') {
            datasetPayload = {
                parentDataset: { id: parentDataset._id, name: parentDataset.name },
                subDatasets: [], // Empty array for single mode
            };
        } else { // hierarchy mode
            // Sub datasets are the rest of the array (excluding the parent itself)
            const subDatasets = finalRelevantDatasets.slice(1).filter(d => d._id !== parentDataset._id);
            datasetPayload = {
                parentDataset: { id: parentDataset._id, name: parentDataset.name },
                subDatasets: subDatasets.map(d => ({ id: d._id, name: d.name })),
            };
        }
    } else {
        // creationMode is 'default', so datasetPayload remains null
        datasetPayload = null;
    }

    // --- Agent Connections Payload Preparation ---
    let agentConnectionsPayload = null;
    if (agentMode === 'swarm') {
        // Get IDs of source nodes connected TO the current agent node
        const connectedAgentIds = agentEdges
            .filter(edge => edge.target === CURRENT_AGENT_NODE_ID && edge.source !== CURRENT_AGENT_NODE_ID)
            .map(edge => edge.source);
        agentConnectionsPayload = {
            mode: 'swarm',
            connectsTo: connectedAgentIds, // Array of agent IDs this new agent receives info from (conceptually)
        };
    } else { // agentMode is 'single'
         agentConnectionsPayload = {
             mode: 'single',
             connectsTo: [], // Empty array for single mode
         };
    }

    // --- Prepare Request Body ---
    const requestBody = {
          agentName: agentName.trim(),
          agentPurpose: agentPurpose.trim(),
          keyResponsibilities: keyResponsibilities.trim(),
          communicationStyle: communicationStyle.trim(),
          constraintsAndEthics: constraintsAndEthics.trim(),
          logoUrl: logoUrl.trim() || null, // Send null if empty string
          cid: cid,
          modelId: selectedModelId,
          datasets: datasetPayload, // Will be null if creationMode is 'default'
          agentConnections: agentConnectionsPayload,
          advancedFirewallEnabled: firewallEnabled,
          isDefaultAgent: creationMode === 'default', // Flag for backend
          type: creationMode === 'default' ? 'brick' : 'normal' // Add type field
    };

    console.log("Submitting Agent Data:", JSON.stringify(requestBody, null, 2)); // Log payload before sending

    // --- API Call ---
    try {
      const response = await axios.post(ADD_AGENT_URL, requestBody, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Process successful response
      const result = response.data;
      if (!result.agentId || !result.agentLink || !result.componentAgentLink || !result.secret) {
          console.error("Incomplete success response from server:", result);
          throw new Error('Incomplete server response after agent creation. Missing required fields (agentId, agentLink, componentAgentLink, secret).');
      }

      // Update state with results
      setGeneratedUrls({
          agentId: result.agentId,
          agentLink: result.agentLink,
          componentAgentLink: result.componentAgentLink
      });
      setSecret(result.secret);
      setGeneratedScript(`<script src="${result.componentAgentLink}" data-agent-id="${result.agentId}" async defer></script>`);
      setError(null); // Clear any previous errors on success

      // Scroll to results section
      // Use a slight delay to ensure the section is rendered
      setTimeout(() => {
          const resultsSection = document.getElementById('results-section'); // Add an ID to the results div
          resultsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

    } catch (error) {
        // Handle axios errors
        console.error("Agent Creation Error:", error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred during agent creation.';
        setError(`Backend Error: ${errorMessage}`);
        setIsLoading(false);
    } finally {
        // This block always runs, ensures loading is stopped even if unexpected things happen
        setIsLoading(false);
    }
  }, [
      user?.id, creationMode, agentName, agentPurpose, keyResponsibilities,
      communicationStyle, constraintsAndEthics, logoUrl, selectedModelId,
      datasetMode, relevantDatasets, // Use relevantDatasets instead of individual states
      agentMode, agentEdges, firewallEnabled, // Agent connection and firewall states
      // State Setters needed for outcome/errors
      setError, setIsLoading, setGeneratedUrls, setSecret, setGeneratedScript,
      // Other states read for validation/payload
      allDatasets, isDatasetsLoading, selectedSingleDatasetId, selectedDatasetParentId // Add these if used directly in validation logic
  ]);


  // --- Copy Button Renderer ---
  const renderCopyButton = (textToCopy) => (
    <ClickableDiv onClick={() => handleCopy(textToCopy)} title={isCopied && lastCopiedText === textToCopy ? "Copied!" : "Copy"} variant='ghost' size='sm' className="!p-1 !-m-1 !text-gray-500 hover:!text-purple-400 hover:!bg-gray-800/50 !rounded !border-none !shadow-none">
        {isCopied && lastCopiedText === textToCopy ? <Check className="w-3.5 h-3.5 text-green-500" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
    </ClickableDiv>
  );

   // --- Determine Submit Button Disabled State ---
   const isSubmitDisabled = () => {
       const genericLoading = isLoading || isDatasetsLoading || isLoadingAgents || isClonableAgentsLoading;
       const requiredFieldsMissing = !agentName.trim() || !agentPurpose.trim() || !keyResponsibilities.trim() || !communicationStyle.trim() || !constraintsAndEthics.trim();
       const modelMissing = !selectedModelId;
       const waitingForInitialChoice = creationMode === 'initial' || creationMode === 'clone_pending';
       // Disable slightly differently for clone_select: disable if no agent is selected for cloning yet
       const waitingForCloneSelection = creationMode === 'clone_select' && !selectedAgentToCloneId;

       if (genericLoading || requiredFieldsMissing || modelMissing || waitingForInitialChoice || waitingForCloneSelection) return true;

       // Dataset validation only if NOT in default mode
       if (creationMode !== 'default') {
            const noDatasetsAvailable = allDatasets.length === 0 && !isDatasetsLoading; // Check if loaded but empty
            // Check if dataset configuration is incomplete based on the mode
            const datasetConfigMissing = (datasetMode === 'single' && !selectedSingleDatasetId) || (datasetMode === 'hierarchy' && !selectedDatasetParentId);

            // Disable if datasets haven't loaded, are empty, or the config for the chosen mode is incomplete
            if (isDatasetsLoading || noDatasetsAvailable || datasetConfigMissing) return true;
       }

       return false; // Otherwise, enable the button
   };

   const getSubmitButtonTitle = () => {
        if (isLoading) return "Generating Agent...";
        if (isDatasetsLoading) return "Loading Datasets...";
        if (isLoadingAgents) return "Loading Your Agents...";
        if (isClonableAgentsLoading) return "Loading Agents to Clone...";

        if (creationMode === 'initial') return "Choose how to start";
        if (creationMode === 'clone_pending') return "Fetching agents...";
        // Show Load Data button title if applicable, not Submit title
        // if (creationMode === 'clone_select' && !selectedAgentToCloneId) return "Select an agent to clone";

        if (!agentName.trim() || !agentPurpose.trim() || !keyResponsibilities.trim() || !communicationStyle.trim() || !constraintsAndEthics.trim()) return "Fill all required fields (*)";
        if (!selectedModelId) return "Select an AI Model";

        if (creationMode !== 'default') {
            if (isDatasetsLoading) return "Loading Datasets..."; // Prioritize loading message
            if (allDatasets.length === 0 && !isDatasetsLoading) return "Cannot create agent without datasets";
            if (datasetMode === 'single' && !selectedSingleDatasetId) return "Select a Dataset";
            if (datasetMode === 'hierarchy' && !selectedDatasetParentId) return "Select & Set Parent Dataset";
        }
        return "Create the AI Agent"; // Default enabled title
   };

   const formRef = useRef(null); // Ref for the form element

   // --- Memoized Model Radio Options ---
   const modelOptions = useMemo(() => Object.values(AI_MODELS).map(model => ({
       value: model.id,
       label: (
           <div className="flex flex-col">
               <span className="font-medium text-gray-300 flex items-center gap-1.5"> {model.name} <Tooltip text={model.description}><Info size={14} className="text-gray-500 hover:text-gray-300"/></Tooltip> </span>
               <span className="text-xs text-gray-500 mt-0.5"> Input: ${model.pricing.input}/M tokens | Output: ${model.pricing.output}/M tokens </span>
               <ClickableLink href={MODEL_PLAYGROUND_URL} target="_blank" title={`Test ${model.name} in Playground`} variant='default' size='sm' className="!text-xs !p-0 !justify-start !mt-0.5 !border-none !shadow-none" onClick={(e) => e.stopPropagation()}> Test in Playground <ExternalLink size={12} className='ml-1'/> </ClickableLink>
           </div>
       ),
  })), []);

   // Memoize dataset options for the dropdown
   const datasetSelectOptions = useMemo(() => {
       if (isDatasetsLoading) {
           return <option value="" disabled>Loading...</option>;
       }
       if (allDatasets.length === 0) {
           return <option value="" disabled>No datasets available</option>;
       }
       return allDatasets.map(d => <option key={d._id} value={d._id}>{d.name}</option>);
   }, [allDatasets, isDatasetsLoading]); // Add isDatasetsLoading dependency

   // Memoize clonable agent options
   const clonableAgentSelectOptions = useMemo(() => {
        if (isClonableAgentsLoading) {
            return <option value="" disabled>Loading agents...</option>;
        }
         if (!clonableAgents || clonableAgents.length === 0) {
            return <option value="" disabled>No agents available to clone</option>;
        }
        return clonableAgents.map(a => <option key={a._id} value={a._id}>{a.agentName}</option>);
   }, [clonableAgents, isClonableAgentsLoading]); // Add isClonableAgentsLoading

  // --- Component Render ---
  return (
    <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
            <TopBar pageTitle={pageTitle} />
            <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black relative bg-black p-6 lg:p-8">
                <div className="relative z-10 h-full max-w-6xl mx-auto">

                    {/* Initial Choice Section */}
                    {creationMode === 'initial' && (
                        <div className="bg-gray-950 shadow-lg ring-1 ring-gray-700/50 rounded-xl p-6 md:p-8 text-center max-w-3xl mx-auto">
                            <h2 className="text-xl font-semibold text-gray-100 mb-2">How would you like to start?</h2>
                            <p className="text-sm text-gray-400 mb-8">Choose an option below to begin creating your new AI Agent.</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <ClickableDiv onClick={handleStartScratch} variant='secondary' size='lg' className="!flex !flex-col !items-center !gap-3 !rounded-lg !px-4 !py-6 !h-full hover:!border-purple-500 hover:!bg-gray-700/50"> <FileText size={28} className='text-purple-400'/> <span className='font-semibold text-base text-gray-100'>Start from Scratch</span> <span className="text-xs font-normal text-gray-400 text-center">Build a new agent configuration field by field.</span> </ClickableDiv>
                                <ClickableDiv onClick={handleStartClone} variant='secondary' size='lg' className="!flex !flex-col !items-center !gap-3 !rounded-lg !px-4 !py-6 !h-full hover:!border-blue-500 hover:!bg-gray-700/50"> <GitBranch size={28} className='text-blue-400'/> <span className='font-semibold text-base text-gray-100'>Clone Existing Agent</span> <span className="text-xs font-normal text-gray-400 text-center">Use an existing agent as a template (datasets/connections excluded).</span> </ClickableDiv>
                                <ClickableDiv onClick={handleStartDefault} variant='secondary' size='lg' className="!flex !flex-col !items-center !gap-3 !rounded-lg !px-4 !py-6 !h-full hover:!border-yellow-500 hover:!bg-gray-700/50"> <AlertTriangle size={28} className='text-yellow-400'/> <span className='font-semibold text-base text-gray-100'>Use Default Data</span> <span className="text-xs font-normal text-gray-400 text-center">Create a generic agent without specific dataset knowledge.</span> </ClickableDiv>
                            </div>
                        </div>
                    )}

                    {/* Clone Agent Selection Section */}
                    {(creationMode === 'clone_pending' || creationMode === 'clone_select') && (
                        <div className="bg-gray-950 shadow-lg ring-1 ring-gray-700/50 rounded-xl p-6 md:p-8 max-w-xl mx-auto space-y-5">
                             <h2 className="text-lg font-semibold text-gray-100 mb-1 flex items-center gap-2"><GitBranch size={20}/> Clone Existing Agent</h2>
                             {isClonableAgentsLoading && ( <div className="flex justify-center items-center text-gray-400 py-4"> <Loader2 className="w-6 h-6 animate-spin mr-2"/> Loading your agents... </div> )}
                             {clonableAgentsError && !isClonableAgentsLoading && ( <div className="text-red-400 text-sm px-3 py-2 bg-red-900/20 border border-red-700/50 rounded flex items-center gap-2"> <ShieldAlert size={18}/> {clonableAgentsError} </div> )}
                             {/* Show selector only when loading is done, no error, and agents were found */}
                             {creationMode === 'clone_select' && !isClonableAgentsLoading && !clonableAgentsError && clonableAgents.length > 0 && (
                                <>
                                <p className="text-sm text-gray-400">Select an agent to use as a template. Its purpose, responsibilities, communication style, constraints, logo, and model will be copied. You'll need to provide a new name and configure the dataset and connections.</p>
                                <div className="flex flex-col sm:flex-row items-stretch gap-3">
                                    <div className="flex-grow">
                                        <label htmlFor="cloneAgentSelect" className="block text-xs font-medium leading-6 text-gray-400 mb-1">Select Agent to Clone *</label>
                                        <select
                                            id="cloneAgentSelect"
                                            name="cloneAgentSelect"
                                            value={selectedAgentToCloneId}
                                            onChange={(e) => setSelectedAgentToCloneId(e.target.value)}
                                            required
                                            className="block w-full h-full rounded-md border-0 py-1.5 px-3 bg-gray-900 text-gray-200 shadow-sm ring-1 ring-inset ring-gray-700 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            <option value="" disabled>-- Select an Agent --</option>
                                            {clonableAgentSelectOptions}
                                        </select>
                                    </div>
                                     {/* Use the specific handler for loading data */}
                                     <ClickableDiv
                                         onClick={handleCloneAgentConfirm}
                                         disabled={!selectedAgentToCloneId || isLoading} // Disable if no selection or main form is loading
                                         loading={isLoading} // Reflect general loading state if needed
                                         title={!selectedAgentToCloneId ? "Please select an agent first" : "Load selected agent data into the form"}
                                         variant='primary'
                                         size='md'
                                         icon={ShieldCheck}
                                         className="sm:self-end !h-[38px]" // Match height with select
                                     >
                                         Load Data
                                     </ClickableDiv>
                                </div>
                                </>
                             )}
                             {/* Explicit message if loading is done, no error, but no agents found */}
                             {creationMode === 'clone_select' && !isClonableAgentsLoading && !clonableAgentsError && clonableAgents.length === 0 && (
                                <div className="text-yellow-500 text-sm px-3 py-2 bg-yellow-900/20 border border-yellow-700/50 rounded flex items-center gap-2">
                                     <AlertTriangle size={18}/> No existing agents were found in your account to clone. Please <ClickableDiv onClick={handleStartScratch} className="!inline font-semibold underline hover:text-yellow-300 !p-0 !bg-transparent !border-none !shadow-none !text-yellow-400 hover:!text-yellow-300">start from scratch</ClickableDiv>.
                                </div>
                              )}
                             <div className="pt-4 border-t border-gray-700/50 flex justify-start">
                                 <ClickableDiv onClick={() => setCreationMode('initial')} variant='ghost' size='sm' icon={ArrowLeft} disabled={isClonableAgentsLoading}>
                                     Back to Choices
                                </ClickableDiv>
                             </div>
                        </div>
                    )}

                    {/* Main Agent Creation Form (Shown for 'scratch' and 'default' modes) */}
                    {(creationMode === 'scratch' || creationMode === 'default') && (
                            // Assign ref to the form for potential scrolling
                            <form ref={formRef} onSubmit={handleSubmit} noValidate>
                                <div className="space-y-10 divide-y divide-gray-800/50">

                                     {/* Warning for Default Mode */}
                                     {creationMode === 'default' && ( <div className="mb-6 bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4 text-sm text-yellow-300 flex items-start gap-3"> <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400"/> <div> <span className='font-semibold'>Using Default Configuration (No Dataset)</span> <p className='mt-1 text-yellow-400/90 text-xs'>This agent will not use any of your specific data sources. Its knowledge will be based solely on the underlying AI model. It will be less personalized and unable to answer questions based on your documents. The Data Source section is disabled.</p> </div> </div> )}

                                    {/* Agent Identity */}
                                    <div className="pt-4 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
                                         <div className="px-4 sm:px-0"><h2 className="text-base font-semibold leading-7 text-gray-200">Agent Identity</h2><p className="mt-1 text-sm leading-6 text-gray-500">Core details defining your agent's name and primary function.</p></div>
                                         <div className="bg-gray-950 shadow-sm ring-1 ring-gray-700/50 rounded-xl md:col-span-2"><div className="px-4 py-6 sm:p-8 grid grid-cols-1 gap-y-6">
                                             <AwsStyleInput id="agentName" label="Agent Name" required description="Unique name for identification." placeholder="e.g., SupportBot V2" value={agentName} onChange={handleInputChange(setAgentName)} maxLength={50}/>
                                             <AwsStyleInput id="agentPurpose" label="Agent Purpose" required type="textarea" rows={5} description="Primary goal. What problem does it solve?" placeholder="e.g., Answer user questions about Product X..." value={agentPurpose} onChange={handleInputChange(setAgentPurpose)} maxLength={500}/>
                                        </div></div>
                                    </div>

                                    {/* Functionality & Behavior */}
                                    <div className="pt-8 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
                                        <div className="px-4 sm:px-0"><h2 className="text-base font-semibold leading-7 text-gray-200">Functionality & Behavior</h2><p className="mt-1 text-sm leading-6 text-gray-500">Detail what the agent does and how it interacts.</p></div>
                                        <div className="bg-gray-950 shadow-sm ring-1 ring-gray-700/50 rounded-xl md:col-span-2"><div className="px-4 py-6 sm:p-8 grid grid-cols-1 gap-y-6">
                                            <AwsStyleInput id="keyResponsibilities" label="Key Responsibilities" required type="textarea" rows={6} description="Specific tasks. Use bullet points." placeholder="- Explain features..." value={keyResponsibilities} onChange={handleInputChange(setKeyResponsibilities)} maxLength={1000}/>
                                            <AwsStyleInput id="communicationStyle" label="Communication Style" required type="textarea" rows={4} description="Tone, personality, and interaction style." placeholder="e.g., Professional, helpful, concise." value={communicationStyle} onChange={handleInputChange(setCommunicationStyle)} maxLength={500}/>
                                        </div></div>
                                    </div>

                                    {/* AI Model Configuration */}
                                    <div className="pt-8 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
                                        <div className="px-4 sm:px-0"><h2 className="text-base font-semibold leading-7 text-gray-200">AI Model Configuration</h2><p className="mt-1 text-sm leading-6 text-gray-500">Choose the underlying AI model and understand associated costs.</p></div>
                                        <div className="bg-gray-950 shadow-sm ring-1 ring-gray-700/50 rounded-xl md:col-span-2"><div className="px-4 py-6 sm:p-8 space-y-6">
                                            <RadioGroup name="aiModel" title="Select AI Model *" selectedValue={selectedModelId} onChange={setSelectedModelId} options={modelOptions} />
                                            {/* Cost Estimation Display */}
                                            {estimatedCostPerChat !== null && creationMode !== 'default' && (
                                                <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700/50 rounded-md text-sm text-blue-300 flex items-start gap-2">
                                                    <Tooltip text="Based on selected model, dataset size(s), and estimated token usage per interaction (selection pass + response pass). This is a rough estimate. Actual costs may vary." className="flex-shrink-0 mt-0.5"><Info size={16} className="text-blue-400"/></Tooltip>
                                                    <div>Estimated Cost Per Chat: <span className="font-semibold text-blue-200">${estimatedCostPerChat.toFixed(6)}</span>
                                                        <p className="text-xs text-blue-400/80 mt-1">Calculated using {AI_MODELS[selectedModelId]?.name} pricing and {datasetMode === 'hierarchy' ? `${relevantDatasets.length} dataset(s) in hierarchy` : `'${relevantDatasets[0]?.name || 'Selected Dataset'}' size`}.</p>
                                                    </div>
                                                </div>
                                             )}
                                             {/* Prompts for missing info */}
                                             {selectedModelId && creationMode !== 'default' && relevantDatasets.length === 0 && allDatasets.length > 0 && !isDatasetsLoading && (
                                                <p className="text-xs text-yellow-500 mt-1">Select a dataset below to see cost estimate.</p>
                                             )}
                                             {creationMode === 'default' && (
                                                <p className="text-xs text-gray-500 mt-1">Cost estimation requires a selected dataset.</p>
                                             )}
                                         </div></div>
                                    </div>

                                    {/* Constraints & Data Source */}
                                    <div className={`pt-8 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3`}>
                                        <div className="px-4 sm:px-0">
                                            <h2 className="text-base font-semibold leading-7 text-gray-200">Constraints & Data Source</h2>
                                            <p className="mt-1 text-sm leading-6 text-gray-500">Set operational boundaries and define the knowledge source(s).</p>
                                            {/* Indicate disabled state clearly for default mode */}
                                            {creationMode === 'default' && <p className="mt-2 text-xs text-yellow-500 font-medium">(Data Source Disabled)</p>}
                                        </div>
                                        <div className="bg-gray-950 shadow-sm ring-1 ring-gray-700/50 rounded-xl md:col-span-2">
                                            <div className="px-4 py-6 sm:p-8 space-y-6">
                                                  {/* Constraints Input */}
                                                  <AwsStyleInput id="constraintsAndEthics" label="Constraints & Ethics" required type="textarea" rows={5} description="Limitations and ethical guidelines (What *not* to do)." placeholder="- Do not provide financial advice..." value={constraintsAndEthics} onChange={handleInputChange(setConstraintsAndEthics)} maxLength={1000}/>

                                                  {/* Data Source Section (Disabled visually and functionally in default mode) */}
                                                  <div className={`border-t border-gray-700/50 pt-6 ${creationMode === 'default' ? 'opacity-50 pointer-events-none' : ''}`}>
                                                      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                                                          <label className="block text-sm font-medium leading-6 text-gray-300">Data Source {creationMode !== 'default' && <span className="text-red-500 ml-0.5">*</span>}</label>
                                                           <RadioGroup
                                                                name="datasetMode"
                                                                selectedValue={datasetMode}
                                                                onChange={setDatasetMode}
                                                                options={[ { value: 'single', label: 'Single Dataset', icon: Box }, { value: 'hierarchy', label: 'Dataset Hierarchy', icon: Share2 }, ]}
                                                                disabled={isDatasetsLoading || allDatasets.length === 0 || creationMode === 'default'} // Disable if loading, no data, or default mode
                                                            />
                                                      </div>

                                                      {/* Loading/Error/Empty States */}
                                                      {isDatasetsLoading && <div className="mt-4 flex justify-center items-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2"/>Loading Datasets...</div>}
                                                      {datasetError && !isDatasetsLoading && <div className="mt-4 text-red-400 text-xs px-3 py-2 bg-red-900/20 border border-red-700/50 rounded">{datasetError}</div>}
                                                      {!isDatasetsLoading && !datasetError && allDatasets.length === 0 && creationMode !== 'default' && <div className="mt-4 text-yellow-500 text-xs px-3 py-2 bg-yellow-900/20 border border-yellow-700/50 rounded">No datasets found. Create/preprocess data first, or choose 'Use Default'.</div>}

                                                      {/* Single Dataset Selector */}
                                                      {datasetMode === 'single' && !isDatasetsLoading && allDatasets.length > 0 && (
                                                         <div className="mt-2">
                                                             <select
                                                                id="singleDataset"
                                                                name="singleDataset"
                                                                value={selectedSingleDatasetId}
                                                                onChange={(e) => setSelectedSingleDatasetId(e.target.value)}
                                                                required={datasetMode === 'single' && creationMode !== 'default'} // Required only in this mode (and not default)
                                                                className="block w-full rounded-md border-0 py-1.5 px-3 bg-gray-900 text-gray-200 shadow-sm ring-1 ring-inset ring-gray-700 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6 disabled:opacity-60 disabled:cursor-not-allowed"
                                                                disabled={allDatasets.length === 0 || creationMode === 'default'} // Also disable in default mode
                                                            >
                                                                 <option value="">-- Select Dataset --</option>
                                                                {datasetSelectOptions}
                                                            </select>
                                                          </div>
                                                       )}

                                                      {/* Dataset Hierarchy Flow */}
                                                      {datasetMode === 'hierarchy' && !isDatasetsLoading && allDatasets.length > 0 && (
                                                          <div className="mt-4 space-y-4">
                                                              <div className="text-xs text-gray-500 space-y-1.5 mb-4 pl-1">
                                                                   <p className="flex items-center gap-1.5"><MousePointerClick size={14}/> 1. Click node.</p>
                                                                   <p className="flex items-center gap-1.5"><UserCheck size={14}/> 2. Click "Set as Parent".</p>
                                                                   <p className="flex items-center gap-1.5"><LinkIcon size={14}/> 3. Drag handles from child {'->'} parent.</p>
                                                               </div>
                                                              <div className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-md flex items-center justify-between gap-3 flex-wrap">
                                                                  <div className='flex items-center gap-3'>
                                                                       <ClickableDiv
                                                                           onClick={handleSetDatasetParent}
                                                                           disabled={!selectedNodeForDatasetParent || selectedNodeForDatasetParent.id === selectedDatasetParentId || creationMode === 'default'}
                                                                           title={!selectedNodeForDatasetParent ? 'Select a dataset node first' : (selectedNodeForDatasetParent.id === selectedDatasetParentId ? 'This node is already the parent' : `Set '${selectedNodeForDatasetParent.data.label}' as Parent`)}
                                                                           variant='primary'
                                                                           size='sm'
                                                                           icon={UserCheck}
                                                                       >
                                                                            Set as Parent
                                                                        </ClickableDiv>
                                                                        {/* Show which node is temporarily selected */}
                                                                        {selectedNodeForDatasetParent && (
                                                                            <span className="text-xs text-gray-400 italic">Selected: <span className="font-medium text-gray-300">{selectedNodeForDatasetParent.data.label}</span></span>
                                                                         )}
                                                                  </div>
                                                                   {/* Show the currently confirmed parent */}
                                                                   {selectedDatasetParentId && (
                                                                      <span className="text-xs font-medium text-purple-300 px-2 py-0.5 bg-purple-900/50 border border-purple-700/50 rounded">Parent: {datasetNodes.find(n => n.id === selectedDatasetParentId)?.data?.label || '...'}</span>
                                                                    )}
                                                              </div>
                                                              {/* React Flow Instance for Datasets */}
                                                              <div ref={datasetFlowWrapper} className="w-full h-[400px] bg-gradient-to-br from-gray-900 to-black rounded-md border border-gray-700/50 relative shadow-inner overflow-hidden">
                                                                   <ReactFlow
                                                                       nodes={datasetNodes}
                                                                       edges={datasetEdges}
                                                                       onNodesChange={onDatasetNodesChange}
                                                                       onEdgesChange={onDatasetEdgesChange}
                                                                       onConnect={onDatasetConnect}
                                                                       isValidConnection={isValidDatasetConnection}
                                                                       onNodeClick={onDatasetNodeClick}
                                                                       onInit={setDatasetRfInstance}
                                                                       nodeTypes={nodeTypes}
                                                                       fitView
                                                                       fitViewOptions={{ padding: 0.3, maxZoom: 1.2 }}
                                                                       deleteKeyCode={['Backspace', 'Delete']}
                                                                       connectionLineStyle={{ stroke: '#a855f7', strokeWidth: 1.5 }}
                                                                       connectionLineType="smoothstep"
                                                                       proOptions={{ hideAttribution: true }}
                                                                   >
                                                                        <Controls position="bottom-right" className="!bg-gray-800 !border-gray-700" />
                                                                        <Background variant="dots" gap={24} size={0.5} color="#374151" />
                                                                        <MiniMap nodeStrokeWidth={3} nodeColor={n => n.data?.isParent ? '#a855f7' : '#555'} position="top-right" className="!bg-gray-800/80 !border-gray-700/50"/>
                                                                    </ReactFlow>
                                                                     {/* Prompt to select parent */}
                                                                    {!selectedDatasetParentId && datasetNodes.length > 0 && creationMode !== 'default' && (
                                                                        <div className="absolute top-3 left-3 bg-yellow-900/80 border border-yellow-700/50 text-yellow-300 text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5 z-10 shadow-lg animate-pulse">
                                                                            <Info size={14}/> Select node & "Set as Parent".
                                                                        </div>
                                                                    )}
                                                              </div>
                                                          </div>
                                                      )}

                                                       {/* Dataset Size Info & Content Viewer */}
                                                       {relevantDatasets.length > 0 && creationMode !== 'default' && (
                                                            <DatasetSizeInfo datasets={relevantDatasets} mode={datasetMode} creationMode={creationMode} />
                                                        )}
                                                        {relevantDatasets.length > 0 && creationMode !== 'default' && (
                                                            <div className="mt-4 border-t border-gray-700/50 pt-4">
                                                                <ClickableDiv onClick={toggleShowDatasetContent} variant='secondary' size='sm' icon={showDatasetContent ? EyeOff : Eye} className="mb-3" title={showDatasetContent ? 'Hide Dataset Content' : 'Show Dataset Content'} >
                                                                    {showDatasetContent ? 'Hide' : 'Show'} Selected {datasetMode === 'hierarchy' ? 'Parent ' : ''}Dataset Content
                                                                </ClickableDiv>
                                                                 {showDatasetContent && (
                                                                    // Show content of the first relevant dataset (parent in hierarchy, or the single selected)
                                                                    <div className="mt-2 p-4 bg-gray-900/50 border border-gray-700/50 rounded-lg">
                                                                        <DatasetContentViewer dataset={relevantDatasets[0]} />
                                                                    </div>
                                                                )}
                                                             </div>
                                                        )}
                                                  </div> {/* End Data Source Section */}
                                            </div> {/* End Card Content */}
                                        </div> {/* End Card */}
                                    </div> {/* End Grid Row */}

                                     {/* Agent Collaboration */}
                                    <div className={`pt-8 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3`}>
                                        <div className="px-4 sm:px-0">
                                            <h2 className="text-base font-semibold leading-7 text-gray-200">Agent Collaboration <span className='text-xs text-gray-500'>(Optional)</span></h2>
                                            <p className="mt-1 text-sm leading-6 text-gray-500">Connect this new agent to existing agents to enable potential swarm behavior (if supported by backend).</p>
                                        </div>
                                        <div className="bg-gray-950 shadow-sm ring-1 ring-gray-700/50 rounded-xl md:col-span-2">
                                            <div className="px-4 py-6 sm:p-8 space-y-6">
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                    <label className="block text-sm font-medium leading-6 text-gray-300">Collaboration Mode</label>
                                                     <RadioGroup
                                                        name="agentMode"
                                                        selectedValue={agentMode}
                                                        onChange={setAgentMode}
                                                        options={[ { value: 'single', label: 'Standalone Agent', icon: Brain }, { value: 'swarm', label: 'Connect Agents (Swarm)', icon: Network }, ]}
                                                        disabled={isLoadingAgents} // Disable while loading agents
                                                    />
                                                </div>
                                                {/* Loading/Error/Empty States for Agents */}
                                                {isLoadingAgents && <div className="mt-4 flex justify-center items-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2"/>Loading Agents...</div>}
                                                {agentFetchError && !isLoadingAgents && <div className="mt-4 text-red-400 text-xs px-3 py-2 bg-red-900/20 border border-red-700/50 rounded">{agentFetchError}</div>}
                                                {!isLoadingAgents && !agentFetchError && agentMode === 'swarm' && existingAgents.length === 0 && <div className="mt-4 text-yellow-500 text-xs px-3 py-2 bg-yellow-900/20 border border-yellow-700/50 rounded">No other agents found in your account to connect with.</div>}

                                                {/* Agent Connection Flow */}
                                                {agentMode === 'swarm' && !isLoadingAgents && existingAgents.length > 0 && (
                                                    <div className="mt-4 space-y-4">
                                                        <div className="text-xs text-gray-500 space-y-1.5 mb-4 pl-1">
                                                             <p className="flex items-center gap-1.5"><Network size={14}/> Drag handles from existing agent {'->'} current agent.</p>
                                                             <p className='italic'>Note: Swarm functionality is dependent on backend implementation.</p>
                                                         </div>
                                                         {/* React Flow Instance for Agents */}
                                                        <div ref={agentFlowWrapper} className="w-full h-[400px] bg-gradient-to-br from-gray-900 via-black to-blue-900/20 rounded-md border border-gray-700/50 relative shadow-inner overflow-hidden">
                                                             <ReactFlow
                                                                nodes={agentNodes}
                                                                edges={agentEdges}
                                                                onNodesChange={onAgentNodesChange}
                                                                onEdgesChange={onAgentEdgesChange}
                                                                onConnect={onAgentConnect}
                                                                isValidConnection={isValidAgentConnection}
                                                                onInit={setAgentRfInstance}
                                                                nodeTypes={nodeTypes}
                                                                fitView
                                                                fitViewOptions={{ padding: 0.3, maxZoom: 1.2 }}
                                                                deleteKeyCode={['Backspace', 'Delete']}
                                                                connectionLineStyle={{ stroke: '#2563eb', strokeWidth: 1.5 }}
                                                                connectionLineType="smoothstep"
                                                                proOptions={{ hideAttribution: true }}
                                                            >
                                                                 <Controls position="bottom-right" className="!bg-gray-800 !border-gray-700" />
                                                                 <Background variant="dots" gap={24} size={0.5} color="#4b5563" />
                                                                 <MiniMap nodeStrokeWidth={3} nodeColor={n => n.data?.isParent ? '#2563eb' : '#555'} position="top-right" className="!bg-gray-800/80 !border-gray-700/50"/>
                                                             </ReactFlow>
                                                         </div>
                                                    </div>
                                                )}
                                                {/* Message for Standalone Mode */}
                                                {agentMode === 'single' && ( <p className="text-sm text-gray-500 mt-2">Agent will operate independently.</p> )}
                                            </div>
                                        </div>
                                    </div>


                                    {/* Security Addons */}
                                    <div className="pt-8 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
                                        <div className="px-4 sm:px-0"> <h2 className="text-base font-semibold leading-7 text-gray-200">Security Addons <span className='text-xs text-gray-500'>(Optional)</span></h2> <p className="mt-1 text-sm leading-6 text-gray-500">Enhance security with optional addons.</p> </div>
                                        <div className="bg-gray-950 shadow-sm ring-1 ring-gray-700/50 rounded-xl md:col-span-2">
                                            <div className="px-4 py-6 sm:p-8 space-y-4">
                                                <div className="relative flex items-start">
                                                    <div className="flex h-6 items-center"> <input id="firewall-addon" name="firewall-addon" type="checkbox" checked={firewallEnabled} onChange={e => setFirewallEnabled(e.target.checked)} className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-purple-600 focus:ring-purple-600 focus:ring-offset-gray-950"/> </div>
                                                    <div className="ml-3 text-sm leading-6"> <label htmlFor="firewall-addon" className="font-medium text-gray-300 cursor-pointer"> Enable Advanced AI Firewall <span className="ml-2 font-semibold text-green-400">(+$10/month)</span> </label> <p className="text-xs text-gray-500 mt-1">Protect endpoint with enhanced security features.</p> </div>
                                                </div>
                                                {/* Firewall Feature List (conditional) */}
                                                {firewallEnabled && ( <div className="pl-7 mt-2 space-y-1.5 text-xs text-gray-400 border-l border-gray-700 ml-2 pt-1 pb-1"> <p className='font-medium text-gray-300 mb-1.5 flex items-center gap-1.5'><ListChecks size={14}/> Features:</p> <ul className="list-disc list-inside space-y-1"> <li>Prompt Injection Shield</li> <li>PII Masking</li> <li>Rate Limiting</li> <li>Topic/Keyword Filtering</li> <li>Activity Monitoring</li> </ul> </div> )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Branding */}
                                    <div className="pt-8 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
                                        <div className="px-4 sm:px-0"><h2 className="text-base font-semibold leading-7 text-gray-200">Branding <span className='text-xs text-gray-500'>(Optional)</span></h2><p className="mt-1 text-sm leading-6 text-gray-500">Customize appearance in compatible interfaces.</p></div>
                                        <div className="bg-gray-950 shadow-sm ring-1 ring-gray-700/50 rounded-xl md:col-span-2"><div className="px-4 py-6 sm:p-8">
                                            <AwsStyleInput id="logoUrl" label="Company/Agent Logo URL" type="url" description="Direct URL to a logo image (PNG, JPG, SVG)." placeholder="https://example.com/logo.png" value={logoUrl} onChange={handleInputChange(setLogoUrl)}/>
                                        </div></div>
                                     </div>

                                </div> {/* End Sections wrapper */}

                                {/* Error Display Area (Before Submit Button) */}
                                {error && !generatedUrls && (
                                     <div className="mt-6 bg-red-900/30 border border-red-700/50 rounded-lg p-3 text-sm text-red-300 flex items-center gap-2 shadow">
                                         <ShieldAlert className="w-5 h-5 flex-shrink-0"/> <span><strong>Error:</strong> {error}</span>
                                     </div>
                                 )}

                                {/* Action Buttons: Cancel & Submit */}
                                <div className="mt-8 pt-6 border-t border-gray-800/50 flex items-center justify-end gap-x-4">
                                     {/* Show Cancel button only when in form mode (scratch/default) and results aren't shown */}
                                     {!generatedUrls && (creationMode === 'scratch' || creationMode === 'default') && (
                                         <ClickableDiv onClick={() => setCreationMode('initial')} variant='secondary' icon={ArrowLeft} disabled={isLoading}>
                                             Cancel
                                         </ClickableDiv>
                                     )}
                                     {/* Submit Button */}
                                    <ClickableDiv
                                        onClick={handleSubmit} // Use onClick for divs acting as buttons
                                        disabled={isSubmitDisabled()}
                                        loading={isLoading}
                                        title={getSubmitButtonTitle()}
                                        variant='primary'
                                        size='md'
                                        icon={Send}
                                        className="min-w-[150px]" // Give it a min width to prevent layout jumps
                                    >
                                         <span>{isLoading ? 'Generating...' : 'Create Agent'}</span>
                                    </ClickableDiv>
                                </div>
                            </form>
                         )}

                         {/* Results Display Section (Shown after successful creation) */}
                        {generatedUrls && (
                            <div id="results-section" className="mt-10 pt-8 border-t border-gray-800/50 space-y-6">
                                <h2 className="text-lg font-semibold text-green-400 flex items-center gap-2"><Check size={20}/> Agent Created Successfully!</h2>
                                <p className="text-sm text-gray-400">Save the following details securely. The Secret Key is shown only once.</p>

                                {/* Agent ID */}
                                <div className="bg-gray-900 rounded-md border border-gray-700/50 p-4"><div className="flex justify-between items-center mb-1.5"><span className="text-sm font-medium text-gray-400">Agent ID</span>{renderCopyButton(generatedUrls.agentId)}</div><p className="text-sm text-gray-200 font-mono break-all">{generatedUrls.agentId}</p></div>

                                {/* Agent API Endpoint */}
                                <div className="bg-gray-900 rounded-md border border-gray-700/50 p-4"><div className="flex justify-between items-center mb-1.5"><span className="text-sm font-medium text-gray-400">Agent API Endpoint (POST)</span><div className="flex items-center space-x-2">{renderCopyButton(generatedUrls.agentLink)}<ClickableDiv onClick={handleShowAgentLinkInfoPopup} title="Show API Usage Info" variant='ghost' size='sm' className="!p-1 !-m-1 !text-gray-500 hover:!text-purple-400 !rounded !border-none !shadow-none"><Info size={16} /></ClickableDiv></div></div><p className="text-sm text-gray-200 font-mono break-all">{generatedUrls.agentLink}</p><p className="text-xs text-yellow-600 mt-2">Use for programmatic interactions via POST requests.</p></div>

                                {/* Secret Key */}
                                {secret && (
                                     <div className="bg-red-900/20 rounded-md border border-red-700/50 p-4">
                                         <div className="flex justify-between items-center mb-1.5">
                                             <span className="text-sm text-red-300 font-semibold">Secret Key</span>
                                             <ClickableDiv onClick={handleToggleSecretKey} title={showSecret ? "Hide Secret" : "Show Secret"} variant={showSecret ? 'ghost' : 'danger'} size='sm' icon={showSecret ? EyeOff : Eye} className={showSecret ? '!text-red-300 hover:!text-red-200 !bg-transparent' : ''} >{showSecret ? "Hide" : "Show"}</ClickableDiv>
                                         </div>
                                         {showSecret ? (
                                             <div className="flex items-center space-x-2 flex-grow justify-start mt-2">
                                                <p className="text-sm text-red-200 font-mono break-all">{secret}</p>{renderCopyButton(secret)}
                                            </div>
                                         ) : (
                                            <p className="text-xs text-red-400 mt-1 font-medium">⚠️ Important: Click 'Show' to reveal. Copy & save this key now. It will not be shown again after leaving this page.</p>
                                        )}
                                        {showSecret && <p className="text-xs text-red-400 mt-1 font-medium">Ensure this secret is stored securely and not shared.</p>}
                                     </div>
                                 )}

                                 {/* Tester Link */}
                                <div className="bg-gray-900 rounded-md border border-gray-700/50 p-4">
                                    <div className="flex justify-between items-center mb-1.5"><span className="text-sm font-medium text-gray-400">Tester / Interface Link</span>{renderCopyButton(`${TESTER_BASE_URL}/echidna/agent/${generatedUrls.agentId}`)}</div>
                                    <ClickableLink href={`${TESTER_BASE_URL}/echidna/agent/${generatedUrls.agentId}`} target="_blank" rel="noopener noreferrer" variant='default' className="!text-sm !text-purple-400 hover:!text-purple-300 hover:!underline break-all block !justify-start !p-0 !border-none !shadow-none">{`${TESTER_BASE_URL}/echidna/agent/${generatedUrls.agentId}`}</ClickableLink>
                                    <p className="text-xs text-gray-500 mt-2">Use this link to test your agent interactively in a simple chat interface.</p>
                                </div>

                                 {/* Embeddable Script */}
                                {generatedScript && (
                                    <div className="bg-gray-900 rounded-md border border-gray-700/50 p-4">
                                        <div className="flex justify-between items-center mb-2"><h3 className="text-sm font-medium text-gray-400">Embeddable Chat Widget Script</h3>{renderCopyButton(generatedScript)}</div>
                                        <pre className="text-sm text-gray-300 overflow-x-auto p-3 bg-black/50 rounded mt-1 font-mono scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"><code>{generatedScript}</code></pre>
                                        <p className="text-xs text-gray-500 mt-2">Paste this script tag into your website's HTML, ideally just before the closing <code>&lt;/body&gt;</code> tag, to embed the chat widget.</p>
                                    </div>
                                )}

                                {/* Create Another Agent Button */}
                                <div className="mt-8 pt-6 border-t border-gray-800/50 flex items-center justify-start gap-x-4">
                                     {/* Use handleStartScratch to reset everything and go back to form */}
                                     <ClickableDiv onClick={handleStartScratch} variant='secondary' icon={PlusSquare}>
                                         Create Another Agent
                                    </ClickableDiv>
                                 </div>
                            </div>
                        )}

                        {/* API Usage Info Popup */}
                        {showAgentLinkInfo && generatedUrls?.agentLink && secret && (
                            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
                                <div className="bg-gray-950 border border-gray-800/50 rounded-lg p-6 max-w-3xl w-full relative flex flex-col max-h-[90vh] shadow-xl">
                                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-800/50 flex-shrink-0">
                                        <h4 className="text-lg font-semibold text-gray-200">Using the Agent API Endpoint</h4>
                                        <ClickableDiv onClick={handleCloseAgentLinkInfoPopup} variant='ghost' size='sm' className="!p-1 !-m-1 text-gray-500 hover:text-gray-300 cursor-pointer rounded hover:!bg-gray-800/50 !border-none !shadow-none" aria-label="Close popup"><X size={20} /></ClickableDiv>
                                    </div>
                                    <div className="overflow-y-auto pr-2 space-y-5 flex-grow scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-gray-950">
                                        <p className="text-sm text-gray-400">To interact with your agent programmatically, send POST requests to the following endpoint with the specified JSON body structure.</p>
                                        <div className="space-y-1.5">
                                            <h5 className="text-sm font-medium text-gray-300">Endpoint URL (POST):</h5>
                                            <pre className="text-sm text-purple-400 p-2 bg-black/50 rounded font-mono break-all"><code>{generatedUrls.agentLink}</code></pre>
                                        </div>
                                        <div className="space-y-1.5">
                                            <h5 className="text-sm font-medium text-gray-300">Required JSON Body:</h5>
                                            <pre className="text-sm p-3 bg-black/50 rounded font-mono"><code>{`{\n  "secret": "YOUR_AGENT_SECRET_KEY",\n  "message": "User's message or query...",\n  "history": [ { "sender": "user", "text": "Previous message" }, { "sender": "ai", "text": "Previous response" } ] // Optional\n}`}</code></pre>
                                            <p className="text-xs text-gray-500">Replace <code className="text-xs bg-gray-700/50 px-1 py-0.5 rounded">YOUR_AGENT_SECRET_KEY</code> with the secret key generated for this agent. Provide the user's input in the <code className="text-xs bg-gray-700/50 px-1 py-0.5 rounded">message</code> field. The <code className="text-xs bg-gray-700/50 px-1 py-0.5 rounded">history</code> array is optional for maintaining conversation context.</p>
                                        </div>
                                        <div className="space-y-3">
                                            <h5 className="text-base font-semibold text-gray-300 mt-4">Example Usage</h5>
                                            <div>
                                                <h6 className="text-sm font-medium text-gray-400 mb-1">cURL:</h6>
                                                <pre className="text-sm p-3 bg-black/50 rounded font-mono scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-black overflow-x-auto"><code>{`curl -X POST ${generatedUrls.agentLink} \\\n     -H "Content-Type: application/json" \\\n     -d '{\n       "secret": "${secret}",\n       "message": "Hello, tell me about your main features."\n     }'`}</code></pre>
                                            </div>
                                            <div>
                                                <h6 className="text-sm font-medium text-gray-400 mb-1">JavaScript (axios):</h6>
                                                <pre className="text-sm p-3 bg-black/50 rounded font-mono scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-black overflow-x-auto"><code>{`const agentEndpoint = "${generatedUrls.agentLink}";
const agentSecret = "${secret}"; // Store securely, not hardcoded in client-side JS!
const userMessage = "What is the return policy?";
const conversationHistory = []; // Maintain history array if needed

axios.post(agentEndpoint, {
  secret: agentSecret,
  message: userMessage,
  history: conversationHistory, // Send history if applicable
}, {
  headers: {
    'Content-Type': 'application/json',
  }
})
.then(response => {
  console.log('Agent response:', response.data.message);
  // Add user message and AI response to conversationHistory for next turn
  // conversationHistory.push({ sender: 'user', text: userMessage });
  // conversationHistory.push({ sender: 'ai', text: response.data.message });
})
.catch(error => {
  // Handle error response data if available
  const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
  console.error('Error calling agent:', errorMessage);
});`}</code></pre>
                                                <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1"><AlertTriangle size={12}/> Important: Never expose your agent secret directly in client-side JavaScript. This example is illustrative; use a backend proxy or serverless function to handle the secret securely.</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-gray-800/50 flex-shrink-0 flex justify-end">
                                        <ClickableDiv onClick={handleCloseAgentLinkInfoPopup} variant='secondary'>Close</ClickableDiv>
                                    </div>
                                </div>
                            </div>
                         )}

                         {/* Default Data Confirmation Modal */}
                        {showDefaultDataWarning && (
                             <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
                                 <div className="bg-gray-950 border border-yellow-700/50 rounded-lg p-6 max-w-lg w-full relative shadow-xl">
                                     <div className="flex items-start gap-3">
                                         <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1"/>
                                         <div>
                                             <h4 className="text-lg font-semibold text-yellow-300">Confirm Use Default Configuration?</h4>
                                             <p className="text-sm text-gray-400 mt-2 mb-4"> Creating an agent without a dataset means it cannot access your specific information and will rely solely on its general knowledge. This may result in less relevant or accurate responses regarding your content. Are you sure you want to proceed? </p>
                                             <div className="flex justify-end gap-3 mt-5">
                                                 <ClickableDiv onClick={cancelUseDefaultData} variant='secondary'> Cancel </ClickableDiv>
                                                 <ClickableDiv onClick={confirmUseDefaultData} variant='warning' icon={ShieldCheck}> Confirm & Proceed </ClickableDiv>
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                             </div>
                         )}

                   </div> {/* End Max Width Container */}
            </main> {/* End Main Content Area */}
        </div> {/* End Main Flex Column */}

        {/* Global styles */}
        <style jsx global>{`
            /* Consistent scrollbar styling */
            .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700 / 0.5) black; }
            .scrollbar-thin::-webkit-scrollbar { width: 6px; height: 6px; }
            .scrollbar-thin::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700 / 0.5); border-radius: 3px; border: 1px solid black; }
            .scrollbar-thin::-webkit-scrollbar-track { background-color: black; }
            /* Specific scrollbar for dataset content */
            .scrollbar-thumb-gray-600::-webkit-scrollbar-thumb { background-color: theme(colors.gray.600); }
            .scrollbar-track-gray-800::-webkit-scrollbar-track { background-color: theme(colors.gray.800); }
            .scrollbar-track-gray-800\/50::-webkit-scrollbar-track { background-color: theme(colors.gray.800 / 0.5); }

            body { background-color: black; margin: 0; font-family: sans-serif; }
            /* Basic animations */
            @keyframes spin { to { transform: rotate(360deg); } }
            .animate-spin { animation: spin 1s linear infinite; }
            @keyframes fade-in-fast { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
            .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
             /* React Flow Customizations */
             .react-flow__node { font-size: 12px; cursor: default; }
             .react-flow__node.react-flow__node-dataset:not([data-isparent="true"]),
             .react-flow__node.react-flow__node-agent:not([data-isparent="true"]) { cursor: grab; } /* Specific grab cursor for non-parent/non-current nodes */
             .react-flow__node.dragging { cursor: grabbing; }

             .react-flow__handle { transition: background 0.15s ease-in-out, border-color 0.15s ease-in-out !important; }
             .react-flow__handle-connecting { background: #c084fc !important; border-color: #a855f7 !important; }
             /* Custom connection line styles */
             .react-flow__connection .react-flow__connection-path[data-testid^="rf__edge-dataset"] { stroke: #a855f7 !important; stroke-width: 1.5px !important;}
             .react-flow__connection .react-flow__connection-path[data-testid^="rf__edge-agent"] { stroke: #2563eb !important; stroke-width: 1.5px !important;}
             .react-flow__connectionline { stroke-dasharray: 4; stroke-width: 1.5px; }
             /* Theme Controls/Minimap */
            .react-flow__controls { background-color: rgba(42, 42, 46, 0.8) !important; border-radius: 4px !important; box-shadow: none !important; border: 1px solid rgba(80, 80, 85, 0.9) !important;}
            .react-flow__controls button { background-color: rgba(60, 60, 64, 0.9) !important; border-bottom: 1px solid rgba(80, 80, 85, 0.9) !important; fill: #bbb !important; }
            .react-flow__controls button:hover { background-color: rgba(70, 70, 75, 0.9) !important; fill: #eee !important; }
            .react-flow__minimap { background-color: rgba(42, 42, 46, 0.8) !important; border-radius: 4px !important; border: 1px solid rgba(80, 80, 85, 0.9) !important; }

            /* Style for form select dropdown arrow */
            select {
              background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
              background-position: right 0.5rem center;
              background-repeat: no-repeat;
              background-size: 1.5em 1.5em;
              padding-right: 2.5rem;
              -webkit-appearance: none;
              -moz-appearance: none;
              appearance: none;
            }
            /* Ensure pointer events work correctly */
            .pointer-events-none { pointer-events: none; }
             /* Ensure whitespace is preserved in pre tags */
             pre { white-space: pre-wrap; word-wrap: break-word; }
             pre code { display: block; } /* Ensure code block respects parent pre */

        `}</style>
    </div> // End Root Flex Div
  );
};

export default Echpilot;